import React, { useEffect, useMemo, useState } from "react";
import { optimizeImageFile } from "../../services/images/optimizeImage";
import { useLocation } from "react-router-dom";
import { useAccessControl } from "../context/useAccessControl";
import { DIAS_SEMANA } from "../../services/local/restaurantProfile";
import {
  PROMOTIONS_UPDATED_EVENT,
  buildPromotionsStorageKey,
  getPromotions,
  reconcilePromotionImagesById,
  savePromotions,
} from "../../services/local/promotions";
import { loadLocalMenuSnapshot } from "../../services/local/menuStorage";
import { getBranchMenuSnapshot } from "../../services/supabase/menuService";
import { isSupabaseConfigured } from "../../services/supabase/config";
import {
  hydrateBranchPromotionsToLocal,
  saveBranchPromotions,
} from "../../services/supabase/promotionsService";

const currencyFormatter = new Intl.NumberFormat("es-AR", {
  style: "currency",
  currency: "ARS",
  maximumFractionDigits: 0,
});
const PROMOS_DRAFT_PREFIX = "mozzoapp:promos:draft:";
const PROMOS_DRAFT_SESSION_IMAGE_PREFIX = "mozzoapp:promos:draft-images:";
const promosDraftSessionCache = new Map();

const PROMOTION_FONT_OPTIONS = [
  {
    id: "inter",
    label: "Inter",
    value: 'Inter, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
  },
  {
    id: "display",
    label: "Bebas Neue",
    value: '"Bebas Neue", "Franklin Gothic Medium", "Arial Narrow", Arial, sans-serif',
  },
  {
    id: "serif",
    label: "Cormorant",
    value: '"Cormorant Garamond", Georgia, "Times New Roman", serif',
  },
  {
    id: "script",
    label: "Pacifico",
    value: '"Pacifico", "Brush Script MT", cursive',
  },
  {
    id: "custom",
    label: "Custom",
    value: "",
  },
];

const createDefaultPromotionSchedule = () =>
  DIAS_SEMANA.map((day) => ({
    id: day.id,
    label: day.label,
    active: false,
    allDay: false,
    apertura: "08:00",
    cierre: "12:00",
  }));

const normalizePromotionSchedule = (schedule) =>
  DIAS_SEMANA.map((day) => {
    const found = Array.isArray(schedule)
      ? schedule.find((item) => item?.id === day.id)
      : null;

    return {
      id: day.id,
      label: day.label,
      active: Boolean(found?.active),
      allDay: Boolean(found?.allDay),
      apertura: found?.apertura || "08:00",
      cierre: found?.cierre || "12:00",
    };
  });

const clonePromotionSchedule = (schedule) =>
  normalizePromotionSchedule(schedule).map((day) => ({ ...day }));

const buildPromotionScheduleSummary = (schedule) => {
  const normalized = normalizePromotionSchedule(schedule);
  const activeDays = normalized.filter((day) => day.active);

  if (activeDays.length === 0) {
    return "Disponible todos los dias";
  }

  return activeDays
    .map((day) =>
      day.allDay ? `${day.label} todo el dia` : `${day.label} ${day.apertura} a ${day.cierre}`
    )
    .join(" · ");
};

const getPromotionFontFamily = (promotion = {}) => {
  if (promotion.fontPreset === "custom") {
    return promotion.customFontFamily?.trim() || PROMOTION_FONT_OPTIONS[0].value;
  }

  return (
    PROMOTION_FONT_OPTIONS.find((option) => option.id === promotion.fontPreset)?.value ||
    promotion.fontFamily ||
    PROMOTION_FONT_OPTIONS[0].value
  );
};

const resizePromotionImageFile = (file) => optimizeImageFile(file, { profile: "promotion" });

const initialComboForm = {
  paymentCondition: "any",
  title: "",
  hook: "",
  categoryId: "",
  validity: "",
  detail: "",
  imageUrl: "",
  fontPreset: "inter",
  customFontFamily: "",
  comboPrice: "",
  deliveryAvailable: false,
  schedule: createDefaultPromotionSchedule(),
};

const initialDailyMenuForm = {
  paymentCondition: "any",
  title: "",
  detail: "",
  imageUrl: "",
  fontPreset: "inter",
  customFontFamily: "",
  comboPrice: "",
  deliveryAvailable: false,
};

const initialPromotionRuleForm = {
  paymentCondition: "any",
  title: "",
  hook: "",
  scopeType: "subcategory",
  categoryId: "",
  subcategoryId: "",
  productId: "",
  discountType: "percentage",
  discountValue: "",
  validity: "",
  detail: "",
  imageUrl: "",
  fontPreset: "inter",
  customFontFamily: "",
  deliveryAvailable: false,
  schedule: createDefaultPromotionSchedule(),
};

const padPromotionDateSegment = (value) => String(value || 0).padStart(2, "0");

const buildLocalDateKey = (date = new Date()) =>
  `${date.getFullYear()}-${padPromotionDateSegment(date.getMonth() + 1)}-${padPromotionDateSegment(
    date.getDate()
  )}`;

const isDailyMenuRecord = (record) =>
  String(record?.comboKind || "").trim().toLowerCase() === "daily_menu";

const getRecordType = (record) => (record?.type === "promotion" ? "promotion" : "combo");

const formatCurrency = (value) => currencyFormatter.format(Number(value || 0));

const formatDiscountSummary = (discountType, discountValue) => {
  const numericValue = Number(discountValue || 0);
  if (discountType === "fixed") {
    return `-${formatCurrency(numericValue)}`;
  }
  return `${numericValue}% OFF`;
};

const resolveProductUnitPrice = (product) => {
  if (!product) {
    return 0;
  }

  if (product.modoPrecio === "manual") {
    return Number(product.precio || 0);
  }

  return Number(product.precioSugerido || 0);
};

const buildPromotionTargetSummary = ({
  scopeType,
  categoryName,
  subcategoryName,
  productName,
}) => {
  switch (scopeType) {
    case "category":
      return categoryName || "Categoria";
    case "subcategory":
      return subcategoryName
        ? `${subcategoryName}${categoryName ? ` · ${categoryName}` : ""}`
        : categoryName || "Subcategoria";
    case "product":
      return productName || "Producto";
    default:
      return categoryName || "Categoria";
  }
};

const buildPromosDraftStorageKey = (branchId) =>
  `${PROMOS_DRAFT_PREFIX}${branchId || "default"}`;

const buildPromosDraftSessionImageStorageKey = (branchId) =>
  `${PROMOS_DRAFT_SESSION_IMAGE_PREFIX}${branchId || "default"}`;

const isDraftImageDataUrl = (value) =>
  typeof value === "string" && value.startsWith("data:");

const keepDraftImageReference = (value) =>
  isDraftImageDataUrl(value) ? "" : value || "";

const buildCompactPromosDraft = (draft) => {
  const nextDraft = {
    ...(draft || {}),
  };

  if (nextDraft.comboForm) {
    nextDraft.comboForm = {
      ...nextDraft.comboForm,
      imageUrl: keepDraftImageReference(nextDraft.comboForm.imageUrl),
    };
  }

  if (nextDraft.dailyMenuForm) {
    nextDraft.dailyMenuForm = {
      ...nextDraft.dailyMenuForm,
      imageUrl: keepDraftImageReference(nextDraft.dailyMenuForm.imageUrl),
    };
  }

  if (nextDraft.promotionRuleForm) {
    nextDraft.promotionRuleForm = {
      ...nextDraft.promotionRuleForm,
      imageUrl: keepDraftImageReference(nextDraft.promotionRuleForm.imageUrl),
    };
  }

  return nextDraft;
};

const loadPromosDraftSessionImages = (branchId) => {
  if (typeof window === "undefined" || !branchId) {
    return {
      comboImageUrl: "",
      dailyMenuImageUrl: "",
      promotionImageUrl: "",
    };
  }

  try {
    const rawValue = window.sessionStorage.getItem(
      buildPromosDraftSessionImageStorageKey(branchId)
    );

    if (!rawValue) {
      return {
        comboImageUrl: "",
        dailyMenuImageUrl: "",
        promotionImageUrl: "",
      };
    }

    const parsedValue = JSON.parse(rawValue);
    return {
      comboImageUrl: parsedValue?.comboImageUrl || "",
      dailyMenuImageUrl: parsedValue?.dailyMenuImageUrl || "",
      promotionImageUrl: parsedValue?.promotionImageUrl || "",
    };
  } catch (error) {
    return {
      comboImageUrl: "",
      dailyMenuImageUrl: "",
      promotionImageUrl: "",
    };
  }
};

const savePromosDraftSessionImages = (branchId, draft) => {
  if (typeof window === "undefined" || !branchId) {
    return;
  }

  const payload = {
    comboImageUrl: isDraftImageDataUrl(draft?.comboForm?.imageUrl)
      ? draft.comboForm.imageUrl
      : "",
    dailyMenuImageUrl: isDraftImageDataUrl(draft?.dailyMenuForm?.imageUrl)
      ? draft.dailyMenuForm.imageUrl
      : "",
    promotionImageUrl: isDraftImageDataUrl(draft?.promotionRuleForm?.imageUrl)
      ? draft.promotionRuleForm.imageUrl
      : "",
  };

  try {
    if (!payload.comboImageUrl && !payload.dailyMenuImageUrl && !payload.promotionImageUrl) {
      window.sessionStorage.removeItem(
        buildPromosDraftSessionImageStorageKey(branchId)
      );
      return;
    }

    window.sessionStorage.setItem(
      buildPromosDraftSessionImageStorageKey(branchId),
      JSON.stringify(payload)
    );
  } catch (error) {
    console.info(
      "No pudimos sostener las imagenes temporales de promos en esta sesion:",
      error
    );
  }
};

const loadPromosDraft = (branchId) => {
  if (typeof window === "undefined" || !branchId) {
    return null;
  }

  try {
    const rawValue = window.localStorage.getItem(buildPromosDraftStorageKey(branchId));
    let parsedValue = rawValue ? JSON.parse(rawValue) : null;

    if (!parsedValue) {
      const sessionValue = window.sessionStorage.getItem(
        buildPromosDraftStorageKey(branchId)
      );
      parsedValue = sessionValue ? JSON.parse(sessionValue) : null;
    }

    if (!parsedValue) {
      parsedValue = promosDraftSessionCache.get(branchId) || null;
    }

    if (!parsedValue || typeof parsedValue !== "object") {
      return null;
    }

    const sessionImages = loadPromosDraftSessionImages(branchId);

    return {
      ...parsedValue,
      comboForm: parsedValue.comboForm
        ? {
            ...parsedValue.comboForm,
            imageUrl:
              parsedValue.comboForm.imageUrl ||
              sessionImages.comboImageUrl ||
              "",
          }
        : parsedValue.comboForm,
      dailyMenuForm: parsedValue.dailyMenuForm
        ? {
            ...parsedValue.dailyMenuForm,
            imageUrl:
              parsedValue.dailyMenuForm.imageUrl ||
              sessionImages.dailyMenuImageUrl ||
              "",
          }
        : parsedValue.dailyMenuForm,
      promotionRuleForm: parsedValue.promotionRuleForm
        ? {
            ...parsedValue.promotionRuleForm,
            imageUrl:
              parsedValue.promotionRuleForm.imageUrl ||
              sessionImages.promotionImageUrl ||
              "",
          }
        : parsedValue.promotionRuleForm,
    };
  } catch (error) {
    return null;
  }
};

const savePromosDraft = (branchId, draft) => {
  if (typeof window === "undefined" || !branchId) {
    return "skipped";
  }

  const storageKey = buildPromosDraftStorageKey(branchId);
  savePromosDraftSessionImages(branchId, draft);
  const compactDraft = buildCompactPromosDraft(draft);
  promosDraftSessionCache.set(branchId, compactDraft);

  try {
    window.localStorage.setItem(storageKey, JSON.stringify(compactDraft));
    try {
      window.sessionStorage.removeItem(storageKey);
    } catch (error) {
      // Si no se puede limpiar la sesion, seguimos igual.
    }
    return "full";
  } catch (error) {
    try {
      window.sessionStorage.setItem(storageKey, JSON.stringify(compactDraft));
      return "session";
    } catch (sessionError) {
      console.warn(
        "No pudimos guardar el borrador persistente de promos. Lo sostenemos en memoria para esta sesion.",
        sessionError
      );
      return "memory";
    }
  }
};

const normalizeComboFormDraft = (draft) => ({
  ...initialComboForm,
  ...(draft || {}),
  schedule: normalizePromotionSchedule(draft?.schedule),
});

const normalizeDailyMenuFormDraft = (draft) => ({
  ...initialDailyMenuForm,
  ...(draft || {}),
});

const normalizePromotionRuleFormDraft = (draft) => ({
  ...initialPromotionRuleForm,
  ...(draft || {}),
  schedule: normalizePromotionSchedule(draft?.schedule),
});

const resolveDraftImageFallback = (branchId, target) => {
  const sessionImages = loadPromosDraftSessionImages(branchId);

  return target === "promotion"
    ? sessionImages.promotionImageUrl || ""
    : target === "dailyMenu"
      ? sessionImages.dailyMenuImageUrl || ""
    : sessionImages.comboImageUrl || "";
};

function Promos() {
  const location = useLocation();
  const {
    canAccessModule,
    canPerformAction,
    currentBranch,
    currentAccessRoleLabel,
    currentRestaurant,
    isCreatorMode,
  } = useAccessControl();
  const promotionsStorageScope = useMemo(
    () => currentBranch || currentRestaurant || null,
    [currentBranch, currentRestaurant]
  );
  const [activeTab, setActiveTab] = useState("combos");
  const [menuLoading, setMenuLoading] = useState(false);
  const [menuError, setMenuError] = useState("");
  const [menuSnapshot, setMenuSnapshot] = useState({
    categories: [],
    subcategoriasPorCategoria: {},
    products: [],
  });
  const [promotions, setPromotions] = useState(() => getPromotions(promotionsStorageScope));
  const [feedback, setFeedback] = useState({ type: "", text: "" });
  const [comboForm, setComboForm] = useState(initialComboForm);
  const [comboImageDraftUrl, setComboImageDraftUrl] = useState("");
  const [comboImageDraftName, setComboImageDraftName] = useState("");
  const [comboImageProcessing, setComboImageProcessing] = useState(false);
  const [comboSearch, setComboSearch] = useState("");
  const [comboProductId, setComboProductId] = useState("");
  const [comboProductQuantity, setComboProductQuantity] = useState("1");
  const [comboItems, setComboItems] = useState([]);
  const [dailyMenuForm, setDailyMenuForm] = useState(initialDailyMenuForm);
  const [dailyMenuImageDraftUrl, setDailyMenuImageDraftUrl] = useState("");
  const [dailyMenuImageDraftName, setDailyMenuImageDraftName] = useState("");
  const [dailyMenuImageProcessing, setDailyMenuImageProcessing] = useState(false);
  const [dailyMenuSearch, setDailyMenuSearch] = useState("");
  const [dailyMenuProductId, setDailyMenuProductId] = useState("");
  const [dailyMenuProductQuantity, setDailyMenuProductQuantity] = useState("1");
  const [dailyMenuItems, setDailyMenuItems] = useState([]);
  const [promotionRuleForm, setPromotionRuleForm] = useState(initialPromotionRuleForm);
  const [promotionImageDraftUrl, setPromotionImageDraftUrl] = useState("");
  const [promotionImageDraftName, setPromotionImageDraftName] = useState("");
  const [promotionImageProcessing, setPromotionImageProcessing] = useState(false);
  const [promotionSearch, setPromotionSearch] = useState("");
  const [scheduleModalTarget, setScheduleModalTarget] = useState("");
  const [scheduleDraft, setScheduleDraft] = useState(createDefaultPromotionSchedule());
  const [draftHydrated, setDraftHydrated] = useState(false);
  const [editingComboId, setEditingComboId] = useState("");
  const [editingDailyMenuId, setEditingDailyMenuId] = useState("");
  const [editingPromotionId, setEditingPromotionId] = useState("");
  const [draftSaveMode, setDraftSaveMode] = useState("full");
  const promotionsBranchId = currentBranch?.id || currentRestaurant?.id || "";
  const shouldUseRemotePromotions =
    Boolean(promotionsBranchId) && !isCreatorMode && isSupabaseConfigured;
  const requestedTab = useMemo(() => {
    const searchParams = new URLSearchParams(location.search);
    const nextTab = String(searchParams.get("tab") || "").trim().toLowerCase();

    if (nextTab === "daily_menus" || nextTab === "promotions" || nextTab === "combos") {
      return nextTab;
    }

    return "";
  }, [location.search]);
  const currentLocalDateKey = buildLocalDateKey(new Date());
  const resolvedComboPreviewImageUrl = comboImageDraftUrl || comboForm.imageUrl || "";
  const resolvedDailyMenuPreviewImageUrl =
    dailyMenuImageDraftUrl || dailyMenuForm.imageUrl || "";
  const resolvedPromotionPreviewImageUrl =
    promotionImageDraftUrl || promotionRuleForm.imageUrl || "";

  useEffect(() => {
    let active = true;

    const loadPromotionsCatalog = async () => {
      const cachedPromotions = getPromotions(promotionsStorageScope);

      if (active) {
        setPromotions(Array.isArray(cachedPromotions) ? cachedPromotions : []);
      }

      if (!shouldUseRemotePromotions) {
        return;
      }

      try {
        const remotePromotions = await hydrateBranchPromotionsToLocal(
          promotionsStorageScope || { id: promotionsBranchId },
          promotionsBranchId
        );

        if (!active) {
          return;
        }

        setPromotions(remotePromotions);
      } catch (error) {
        if (!active) {
          return;
        }

        setFeedback((previous) =>
          previous.type === "error" && previous.text
            ? previous
            : {
                type: "error",
                text:
                  error.message ||
                  "No pudimos cargar las promos remotas de la sucursal.",
              }
        );
      }
    };

    loadPromotionsCatalog();

    return () => {
      active = false;
    };
  }, [promotionsStorageScope, promotionsBranchId, shouldUseRemotePromotions]);

  useEffect(() => {
    if (!currentBranch?.id) {
      setDraftHydrated(false);
      return;
    }

    const savedDraft = loadPromosDraft(currentBranch.id);

    if (savedDraft) {
      const nextComboForm = normalizeComboFormDraft(savedDraft.comboForm);
      const nextDailyMenuForm = normalizeDailyMenuFormDraft(savedDraft.dailyMenuForm);
      const nextPromotionRuleForm = normalizePromotionRuleFormDraft(
        savedDraft.promotionRuleForm
      );
      setActiveTab(
        requestedTab ||
          (savedDraft.activeTab === "promotions"
          ? "promotions"
          : savedDraft.activeTab === "daily_menus"
            ? "daily_menus"
            : "combos")
      );
      setComboForm(nextComboForm);
      setComboImageDraftUrl(nextComboForm.imageUrl || "");
      setComboImageDraftName(nextComboForm.imageUrl ? "Imagen del borrador" : "");
      setComboSearch(savedDraft.comboSearch || "");
      setComboProductId(savedDraft.comboProductId || "");
      setComboProductQuantity(savedDraft.comboProductQuantity || "1");
      setComboItems(Array.isArray(savedDraft.comboItems) ? savedDraft.comboItems : []);
      setEditingComboId(savedDraft.editingComboId || "");
      setDailyMenuForm(nextDailyMenuForm);
      setDailyMenuImageDraftUrl(nextDailyMenuForm.imageUrl || "");
      setDailyMenuImageDraftName(
        nextDailyMenuForm.imageUrl ? "Imagen del borrador" : ""
      );
      setDailyMenuSearch(savedDraft.dailyMenuSearch || "");
      setDailyMenuProductId(savedDraft.dailyMenuProductId || "");
      setDailyMenuProductQuantity(savedDraft.dailyMenuProductQuantity || "1");
      setDailyMenuItems(Array.isArray(savedDraft.dailyMenuItems) ? savedDraft.dailyMenuItems : []);
      setEditingDailyMenuId(savedDraft.editingDailyMenuId || "");
      setPromotionRuleForm(nextPromotionRuleForm);
      setPromotionImageDraftUrl(nextPromotionRuleForm.imageUrl || "");
      setPromotionImageDraftName(
        nextPromotionRuleForm.imageUrl ? "Imagen del borrador" : ""
      );
      setPromotionSearch(savedDraft.promotionSearch || "");
      setEditingPromotionId(savedDraft.editingPromotionId || "");
    } else {
      setActiveTab(requestedTab || "combos");
      setComboForm(initialComboForm);
      setComboImageDraftUrl("");
      setComboImageDraftName("");
      setComboSearch("");
      setComboProductId("");
      setComboProductQuantity("1");
      setComboItems([]);
      setEditingComboId("");
      setDailyMenuForm(initialDailyMenuForm);
      setDailyMenuImageDraftUrl("");
      setDailyMenuImageDraftName("");
      setDailyMenuSearch("");
      setDailyMenuProductId("");
      setDailyMenuProductQuantity("1");
      setDailyMenuItems([]);
      setEditingDailyMenuId("");
      setPromotionRuleForm(initialPromotionRuleForm);
      setPromotionImageDraftUrl("");
      setPromotionImageDraftName("");
      setPromotionSearch("");
      setEditingPromotionId("");
    }

    setScheduleModalTarget("");
    setScheduleDraft(createDefaultPromotionSchedule());
    setDraftHydrated(true);
  }, [currentBranch?.id, requestedTab]);

  useEffect(() => {
    if (!requestedTab) {
      return;
    }

    setActiveTab(requestedTab);
  }, [requestedTab]);

  useEffect(() => {
    if (!currentBranch?.id || !draftHydrated) {
      return;
    }

    const nextDraftSaveMode = savePromosDraft(currentBranch.id, {
      activeTab,
      comboForm: {
        ...comboForm,
        imageUrl: resolvedComboPreviewImageUrl,
      },
      comboSearch,
      comboProductId,
      comboProductQuantity,
      comboItems,
      editingComboId,
      dailyMenuForm: {
        ...dailyMenuForm,
        imageUrl: resolvedDailyMenuPreviewImageUrl,
      },
      dailyMenuSearch,
      dailyMenuProductId,
      dailyMenuProductQuantity,
      dailyMenuItems,
      editingDailyMenuId,
      promotionRuleForm: {
        ...promotionRuleForm,
        imageUrl: resolvedPromotionPreviewImageUrl,
      },
      promotionSearch,
      editingPromotionId,
      updatedAt: new Date().toISOString(),
    });

    setDraftSaveMode(nextDraftSaveMode);
  }, [
    activeTab,
    comboForm,
    comboImageDraftUrl,
    comboItems,
    comboProductId,
    comboProductQuantity,
    comboSearch,
    currentBranch?.id,
    dailyMenuForm,
    dailyMenuImageDraftUrl,
    dailyMenuItems,
    dailyMenuProductId,
    dailyMenuProductQuantity,
    dailyMenuSearch,
    draftHydrated,
    editingComboId,
    editingDailyMenuId,
    editingPromotionId,
    promotionImageDraftUrl,
    promotionRuleForm,
    promotionSearch,
    resolvedComboPreviewImageUrl,
    resolvedDailyMenuPreviewImageUrl,
    resolvedPromotionPreviewImageUrl,
  ]);

  useEffect(() => {
    let isMounted = true;

    const loadMenu = async () => {
      if (!currentBranch?.id) {
        if (isMounted) {
          setMenuSnapshot({
            categories: [],
            subcategoriasPorCategoria: {},
            products: [],
          });
        }
        return;
      }

      setMenuLoading(true);
      setMenuError("");

      try {
        const snapshot =
          !isCreatorMode && isSupabaseConfigured
            ? await getBranchMenuSnapshot(currentBranch.id)
            : loadLocalMenuSnapshot(currentBranch.id);

        if (isMounted) {
          setMenuSnapshot(snapshot);
        }
      } catch (error) {
        if (isMounted) {
          setMenuError(error.message || "No pudimos cargar el menu de la sucursal.");
          setMenuSnapshot(loadLocalMenuSnapshot(currentBranch.id));
        }
      } finally {
        if (isMounted) {
          setMenuLoading(false);
        }
      }
    };

    loadMenu();

    return () => {
      isMounted = false;
    };
  }, [currentBranch?.id, isCreatorMode]);

  useEffect(() => {
    const syncPromotions = () => {
      setPromotions(getPromotions(promotionsStorageScope));
    };

    const handlePromotionsUpdated = (event) => {
      const updatedRestaurantId = event.detail?.restaurantId || "default";
      const currentRestaurantId = promotionsStorageScope?.id || "default";

      if (updatedRestaurantId !== currentRestaurantId) {
        return;
      }

      if (Array.isArray(event.detail?.promotions)) {
        setPromotions(event.detail.promotions);
        return;
      }

      syncPromotions();
    };

    const handleStorage = (event) => {
      const expectedKey = buildPromotionsStorageKey(promotionsStorageScope?.id);
      if (event.key !== expectedKey) {
        return;
      }

      if (shouldUseRemotePromotions) {
        void hydrateBranchPromotionsToLocal(
          promotionsStorageScope || { id: promotionsBranchId },
          promotionsBranchId
        )
          .then((remotePromotions) => {
            setPromotions(remotePromotions);
          })
          .catch(() => {
            syncPromotions();
          });
        return;
      }

      syncPromotions();
    };

    window.addEventListener(PROMOTIONS_UPDATED_EVENT, handlePromotionsUpdated);
    window.addEventListener("storage", handleStorage);

    return () => {
      window.removeEventListener(PROMOTIONS_UPDATED_EVENT, handlePromotionsUpdated);
      window.removeEventListener("storage", handleStorage);
    };
  }, [promotionsStorageScope, promotionsBranchId, shouldUseRemotePromotions]);

  const menuCategories = useMemo(() => menuSnapshot.categories || [], [menuSnapshot.categories]);
  const menuProducts = useMemo(() => menuSnapshot.products || [], [menuSnapshot.products]);
  const subcategoriesByCategory = useMemo(
    () => menuSnapshot.subcategoriasPorCategoria || {},
    [menuSnapshot.subcategoriasPorCategoria]
  );

  const comboProductsByCategory = useMemo(() => {
    if (!comboForm.categoryId) {
      return menuProducts;
    }

    const selectedCategoryProducts = menuProducts.filter(
      (product) => product.categoria === comboForm.categoryId
    );
    const remainingProducts = menuProducts.filter(
      (product) => product.categoria !== comboForm.categoryId
    );

    return [...selectedCategoryProducts, ...remainingProducts];
  }, [comboForm.categoryId, menuProducts]);

  const filteredComboProducts = useMemo(() => {
    const query = comboSearch.trim().toLowerCase();
    if (!query) {
      return comboProductsByCategory;
    }

    return comboProductsByCategory.filter((product) =>
      [product.nombre, product.subcategoria, product.descripcion]
        .filter(Boolean)
        .some((value) => value.toLowerCase().includes(query))
    );
  }, [comboProductsByCategory, comboSearch]);

  const selectedComboProduct = useMemo(
    () => menuProducts.find((product) => product.id === comboProductId) || null,
    [comboProductId, menuProducts]
  );

  const selectedComboCategory = useMemo(
    () => menuCategories.find((category) => category.id === comboForm.categoryId) || null,
    [comboForm.categoryId, menuCategories]
  );

  const filteredDailyMenuProducts = useMemo(() => {
    const query = dailyMenuSearch.trim().toLowerCase();

    if (!query) {
      return menuProducts;
    }

    return menuProducts.filter((product) =>
      [product.nombre, product.subcategoria, product.descripcion]
        .filter(Boolean)
        .some((value) => value.toLowerCase().includes(query))
    );
  }, [dailyMenuSearch, menuProducts]);

  const selectedDailyMenuProduct = useMemo(
    () => menuProducts.find((product) => product.id === dailyMenuProductId) || null,
    [dailyMenuProductId, menuProducts]
  );

  const promotionScopeSubcategories = useMemo(() => {
    if (!promotionRuleForm.categoryId) {
      return [];
    }

    return subcategoriesByCategory[promotionRuleForm.categoryId] || [];
  }, [promotionRuleForm.categoryId, subcategoriesByCategory]);

  const promotionScopeProducts = useMemo(() => {
    const query = promotionSearch.trim().toLowerCase();
    let products = menuProducts;

    if (promotionRuleForm.categoryId) {
      products = products.filter((product) => product.categoria === promotionRuleForm.categoryId);
    }

    if (promotionRuleForm.scopeType === "subcategory" && promotionRuleForm.subcategoryId) {
      products = products.filter(
        (product) => product.subcategoriaId === promotionRuleForm.subcategoryId
      );
    }

    if (!query) {
      return products;
    }

    return products.filter((product) =>
      [product.nombre, product.subcategoria, product.descripcion]
        .filter(Boolean)
        .some((value) => value.toLowerCase().includes(query))
    );
  }, [menuProducts, promotionRuleForm.categoryId, promotionRuleForm.scopeType, promotionRuleForm.subcategoryId, promotionSearch]);

  const selectedPromotionCategory = useMemo(
    () =>
      menuCategories.find((category) => category.id === promotionRuleForm.categoryId) || null,
    [menuCategories, promotionRuleForm.categoryId]
  );

  const selectedPromotionSubcategory = useMemo(
    () =>
      promotionScopeSubcategories.find(
        (subcategory) => subcategory.id === promotionRuleForm.subcategoryId
      ) || null,
    [promotionRuleForm.subcategoryId, promotionScopeSubcategories]
  );

  const selectedPromotionProduct = useMemo(
    () => menuProducts.find((product) => product.id === promotionRuleForm.productId) || null,
    [menuProducts, promotionRuleForm.productId]
  );

  const comboRecords = useMemo(
    () =>
      promotions.filter(
        (record) => getRecordType(record) === "combo" && !isDailyMenuRecord(record)
      ),
    [promotions]
  );

  const dailyMenuRecords = useMemo(
    () =>
      promotions
        .filter((record) => isDailyMenuRecord(record))
        .sort((left, right) => {
          const leftDate = String(left?.dailyMenuDate || "").trim();
          const rightDate = String(right?.dailyMenuDate || "").trim();

          if (leftDate !== rightDate) {
            return rightDate.localeCompare(leftDate);
          }

          return new Date(right?.createdAt || 0) - new Date(left?.createdAt || 0);
        }),
    [promotions]
  );

  const promotionRecords = useMemo(
    () => promotions.filter((record) => getRecordType(record) === "promotion"),
    [promotions]
  );

  const activeComboCount = useMemo(
    () => comboRecords.filter((record) => record.active !== false).length,
    [comboRecords]
  );

  const activePromotionCount = useMemo(
    () => promotionRecords.filter((record) => record.active !== false).length,
    [promotionRecords]
  );

  const activeDailyMenuCount = useMemo(
    () =>
      dailyMenuRecords.filter(
        (record) =>
          record.active !== false &&
          String(record.dailyMenuDate || "").trim() === currentLocalDateKey
      ).length,
    [currentLocalDateKey, dailyMenuRecords]
  );

  const comboBaseTotal = useMemo(
    () =>
      comboItems.reduce(
        (total, item) => total + Number(item.unitPrice || 0) * Number(item.quantity || 0),
        0
      ),
    [comboItems]
  );

  const comboPriceValue = Number(comboForm.comboPrice || 0);
  const comboSavings = Math.max(0, comboBaseTotal - comboPriceValue);
  const dailyMenuBaseTotal = useMemo(
    () =>
      dailyMenuItems.reduce(
        (total, item) => total + Number(item.unitPrice || 0) * Number(item.quantity || 0),
        0
      ),
    [dailyMenuItems]
  );
  const dailyMenuPriceValue = Number(dailyMenuForm.comboPrice || 0);
  const dailyMenuSavings = Math.max(0, dailyMenuBaseTotal - dailyMenuPriceValue);
  // Permisos v1.1: promos y combos reutilizan manage_menu para cada mutacion.
  const canManagePromos =
    canAccessModule("promos") && canPerformAction("manage_menu");

  const notifyPromoPermissionDenied = (actionLabel) => {
    setFeedback({
      type: "error",
      text: `${currentAccessRoleLabel} puede revisar promos, pero no ${actionLabel} en esta sucursal.`,
    });
  };

  const persistRecords = async (nextRecords) => {
    try {
      const saved = shouldUseRemotePromotions
        ? await saveBranchPromotions(promotionsBranchId, nextRecords)
        : savePromotions(promotionsStorageScope || { id: promotionsBranchId }, nextRecords);
      const reconciledSaved = shouldUseRemotePromotions
        ? reconcilePromotionImagesById(nextRecords, saved)
        : { records: saved, recovered: 0 };
      let finalRecords = reconciledSaved.records;

      if (shouldUseRemotePromotions && reconciledSaved.recovered > 0) {
        try {
          finalRecords = await saveBranchPromotions(promotionsBranchId, finalRecords);
        } catch (error) {
          finalRecords = reconciledSaved.records;
        }
      }

      if (shouldUseRemotePromotions) {
        savePromotions(promotionsStorageScope || { id: promotionsBranchId }, finalRecords);
        finalRecords = await hydrateBranchPromotionsToLocal(
          promotionsStorageScope || { id: promotionsBranchId },
          promotionsBranchId
        );
      }

      setPromotions(finalRecords);
      return {
        records: finalRecords,
        usedLocalFallback: false,
        recoveredImages: reconciledSaved.recovered,
      };
    } catch (error) {
      const fallbackSaved = savePromotions(
        promotionsStorageScope || { id: promotionsBranchId },
        nextRecords
      );

      setPromotions(fallbackSaved);
      return {
        records: fallbackSaved,
        usedLocalFallback: true,
        recoveredImages: 0,
        error,
      };
    }
  };

  const resetComboComposer = () => {
    setEditingComboId("");
    setComboForm(initialComboForm);
    setComboImageDraftUrl("");
    setComboImageDraftName("");
    setComboImageProcessing(false);
    setComboSearch("");
    setComboProductId("");
    setComboProductQuantity("1");
    setComboItems([]);
  };

  const resetDailyMenuComposer = () => {
    setEditingDailyMenuId("");
    setDailyMenuForm(initialDailyMenuForm);
    setDailyMenuImageDraftUrl("");
    setDailyMenuImageDraftName("");
    setDailyMenuImageProcessing(false);
    setDailyMenuSearch("");
    setDailyMenuProductId("");
    setDailyMenuProductQuantity("1");
    setDailyMenuItems([]);
  };

  const resetPromotionComposer = () => {
    setEditingPromotionId("");
    setPromotionRuleForm(initialPromotionRuleForm);
    setPromotionImageDraftUrl("");
    setPromotionImageDraftName("");
    setPromotionImageProcessing(false);
    setPromotionSearch("");
  };

  const startEditingCombo = (record) => {
    if (!record || !canManagePromos) {
      return;
    }

    setActiveTab("combos");
    setEditingComboId(record.id);
    setComboForm({
      ...initialComboForm,
      paymentCondition: record.paymentCondition === "cash_only" ? "cash_only" : "any",
      title: record.title || "",
      hook: record.hook || "",
      categoryId: record.categoryId || "",
      validity: record.validity || "",
      detail: record.detail || "",
      imageUrl: record.imageUrl || "",
      fontPreset: record.fontPreset || "inter",
      customFontFamily: record.customFontFamily || "",
      comboPrice: String(record.comboPrice || ""),
      deliveryAvailable: record.deliveryAvailable === true,
      schedule: normalizePromotionSchedule(record.schedule),
    });
    setComboImageDraftUrl(record.imageUrl || "");
    setComboImageDraftName(record.imageUrl ? "Imagen actual del combo" : "");
    setComboItems(Array.isArray(record.items) ? record.items.map((item) => ({ ...item })) : []);
    setComboSearch("");
    setComboProductId("");
    setComboProductQuantity("1");
    setFeedback({ type: "", text: "" });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const startEditingDailyMenu = (record) => {
    if (!record || !canManagePromos) {
      return;
    }

    setActiveTab("daily_menus");
    setEditingDailyMenuId(record.id);
    setDailyMenuForm({
      ...initialDailyMenuForm,
      paymentCondition: record.paymentCondition === "cash_only" ? "cash_only" : "any",
      title: record.title || "",
      detail: record.detail || "",
      imageUrl: record.imageUrl || "",
      fontPreset: record.fontPreset || "inter",
      customFontFamily: record.customFontFamily || "",
      comboPrice: String(record.comboPrice || ""),
      deliveryAvailable: record.deliveryAvailable === true,
    });
    setDailyMenuImageDraftUrl(record.imageUrl || "");
    setDailyMenuImageDraftName(record.imageUrl ? "Imagen actual del menu del dia" : "");
    setDailyMenuItems(Array.isArray(record.items) ? record.items.map((item) => ({ ...item })) : []);
    setDailyMenuSearch("");
    setDailyMenuProductId("");
    setDailyMenuProductQuantity("1");
    setFeedback({ type: "", text: "" });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const startEditingPromotion = (record) => {
    if (!record || !canManagePromos) {
      return;
    }

    setActiveTab("promotions");
    setEditingPromotionId(record.id);
    setPromotionRuleForm({
      ...initialPromotionRuleForm,
      paymentCondition: record.paymentCondition === "cash_only" ? "cash_only" : "any",
      title: record.title || "",
      hook: record.hook || "",
      scopeType: record.scopeType || "subcategory",
      categoryId: record.categoryId || "",
      subcategoryId: record.subcategoryId || "",
      productId: record.productId || "",
      discountType: record.discountType || "percentage",
      discountValue: String(record.discountValue || ""),
      validity: record.validity || "",
      detail: record.detail || "",
      imageUrl: record.imageUrl || "",
      fontPreset: record.fontPreset || "inter",
      customFontFamily: record.customFontFamily || "",
      deliveryAvailable: record.deliveryAvailable === true,
      schedule: normalizePromotionSchedule(record.schedule),
    });
    setPromotionImageDraftUrl(record.imageUrl || "");
    setPromotionImageDraftName(record.imageUrl ? "Imagen actual de la promocion" : "");
    setPromotionSearch("");
    setFeedback({ type: "", text: "" });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleComboImageSelected = async (event) => {
    if (!canManagePromos) {
      notifyPromoPermissionDenied("editar combos");
      event.target.value = "";
      return;
    }

    const [file] = Array.from(event.target.files || []);

    if (!file) {
      return;
    }

    try {
      setComboImageProcessing(true);
      const imageUrl = await resizePromotionImageFile(file);
      setComboImageDraftUrl(imageUrl);
      setComboImageDraftName(file.name || "Imagen del combo");
      setComboForm((prev) => ({ ...prev, imageUrl }));
    } catch (error) {
      setFeedback({ type: "error", text: error.message });
    } finally {
      setComboImageProcessing(false);
      event.target.value = "";
    }
  };

  const handlePromotionImageSelected = async (event) => {
    if (!canManagePromos) {
      notifyPromoPermissionDenied("editar promociones");
      event.target.value = "";
      return;
    }

    const [file] = Array.from(event.target.files || []);

    if (!file) {
      return;
    }

    try {
      setPromotionImageProcessing(true);
      const imageUrl = await resizePromotionImageFile(file);
      setPromotionImageDraftUrl(imageUrl);
      setPromotionImageDraftName(file.name || "Imagen de la promocion");
      setPromotionRuleForm((prev) => ({ ...prev, imageUrl }));
    } catch (error) {
      setFeedback({ type: "error", text: error.message });
    } finally {
      setPromotionImageProcessing(false);
      event.target.value = "";
    }
  };

  const handleDailyMenuImageSelected = async (event) => {
    if (!canManagePromos) {
      notifyPromoPermissionDenied("editar menus del dia");
      event.target.value = "";
      return;
    }

    const [file] = Array.from(event.target.files || []);

    if (!file) {
      return;
    }

    try {
      setDailyMenuImageProcessing(true);
      const imageUrl = await resizePromotionImageFile(file);
      setDailyMenuImageDraftUrl(imageUrl);
      setDailyMenuImageDraftName(file.name || "Imagen del menu del dia");
      setDailyMenuForm((prev) => ({ ...prev, imageUrl }));
    } catch (error) {
      setFeedback({ type: "error", text: error.message });
    } finally {
      setDailyMenuImageProcessing(false);
      event.target.value = "";
    }
  };

  const openScheduleModal = (target) => {
    if (!canManagePromos) {
      notifyPromoPermissionDenied("cambiar horarios comerciales");
      return;
    }

    setScheduleModalTarget(target);
    setScheduleDraft(
      clonePromotionSchedule(
        target === "combo" ? comboForm.schedule : promotionRuleForm.schedule
      )
    );
  };

  const closeScheduleModal = () => {
    setScheduleModalTarget("");
    setScheduleDraft(createDefaultPromotionSchedule());
  };

  const updateScheduleDraftDay = (dayId, changes) => {
    setScheduleDraft((prev) =>
      prev.map((day) => (day.id === dayId ? { ...day, ...changes } : day))
    );
  };

  const applyScheduleDraft = () => {
    if (!canManagePromos) {
      notifyPromoPermissionDenied("guardar horarios comerciales");
      return;
    }

    const normalized = normalizePromotionSchedule(scheduleDraft);

    if (scheduleModalTarget === "combo") {
      setComboForm((prev) => ({ ...prev, schedule: normalized }));
    }

    if (scheduleModalTarget === "promotion") {
      setPromotionRuleForm((prev) => ({ ...prev, schedule: normalized }));
    }

    closeScheduleModal();
  };

  const addComboItem = () => {
    if (!canManagePromos) {
      notifyPromoPermissionDenied("armar combos");
      return;
    }

    if (!selectedComboProduct) {
      setFeedback({ type: "error", text: "Selecciona un producto del menu para agregar." });
      return;
    }

    const quantity = Math.max(1, Number(comboProductQuantity || 1));

    setComboItems((prev) => {
      const existing = prev.find((item) => item.productId === selectedComboProduct.id);

      if (existing) {
        return prev.map((item) =>
          item.productId === selectedComboProduct.id
            ? { ...item, quantity: item.quantity + quantity }
            : item
        );
      }

      return [
        ...prev,
        {
          productId: selectedComboProduct.id,
          name: selectedComboProduct.nombre,
          quantity,
          unitPrice: resolveProductUnitPrice(selectedComboProduct),
          subcategoryName: selectedComboProduct.subcategoria || "",
        },
      ];
    });

    setComboProductId("");
    setComboProductQuantity("1");
    setComboSearch("");
    setFeedback({ type: "success", text: "Producto agregado al combo." });
  };

  const addDailyMenuItem = () => {
    if (!canManagePromos) {
      notifyPromoPermissionDenied("armar menus del dia");
      return;
    }

    if (!selectedDailyMenuProduct) {
      setFeedback({ type: "error", text: "Selecciona un producto del menu para agregar." });
      return;
    }

    const quantity = Math.max(1, Number(dailyMenuProductQuantity || 1));

    setDailyMenuItems((prev) => {
      const existing = prev.find((item) => item.productId === selectedDailyMenuProduct.id);

      if (existing) {
        return prev.map((item) =>
          item.productId === selectedDailyMenuProduct.id
            ? { ...item, quantity: item.quantity + quantity }
            : item
        );
      }

      return [
        ...prev,
        {
          productId: selectedDailyMenuProduct.id,
          name: selectedDailyMenuProduct.nombre,
          quantity,
          unitPrice: resolveProductUnitPrice(selectedDailyMenuProduct),
          subcategoryName: selectedDailyMenuProduct.subcategoria || "",
        },
      ];
    });

    setDailyMenuProductId("");
    setDailyMenuProductQuantity("1");
    setDailyMenuSearch("");
    setFeedback({ type: "success", text: "Producto agregado al menu del dia." });
  };

  const removeComboItem = (productId) => {
    if (!canManagePromos) {
      notifyPromoPermissionDenied("editar combos");
      return;
    }

    setComboItems((prev) => prev.filter((item) => item.productId !== productId));
  };

  const removeDailyMenuItem = (productId) => {
    if (!canManagePromos) {
      notifyPromoPermissionDenied("editar menus del dia");
      return;
    }

    setDailyMenuItems((prev) => prev.filter((item) => item.productId !== productId));
  };

  const handleCreateCombo = async (event) => {
    event.preventDefault();

    if (!canManagePromos) {
      notifyPromoPermissionDenied("modificar promociones");
      return;
    }

    const title = comboForm.title.trim();
    const category = selectedComboCategory;
    const detail = comboForm.detail.trim();
    const validity = comboForm.validity.trim();
    const schedule = normalizePromotionSchedule(comboForm.schedule);
    const comboPrice = Number(comboForm.comboPrice || 0);

    if (!title) {
      setFeedback({ type: "error", text: "Ponle un nombre al combo." });
      return;
    }

    if (!category?.id) {
      setFeedback({ type: "error", text: "Elige una categoria para ubicar el combo." });
      return;
    }

    if (!comboItems.length) {
      setFeedback({ type: "error", text: "El combo necesita al menos un producto." });
      return;
    }

    if (comboPrice <= 0) {
      setFeedback({ type: "error", text: "Carga un precio final valido para el combo." });
      return;
    }

    const fontFamily = getPromotionFontFamily(comboForm);
    const savingsAmount = Math.max(0, comboBaseTotal - comboPrice);
    const pricingSummary = savingsAmount
      ? `${formatCurrency(comboPrice)} · Ahorras ${formatCurrency(savingsAmount)}`
      : `${formatCurrency(comboPrice)}`;

    const existingRecord = editingComboId
      ? promotions.find((record) => record.id === editingComboId)
      : null;
    const comboImageUrl =
      comboImageDraftUrl ||
      comboForm.imageUrl ||
      resolveDraftImageFallback(currentBranch?.id, "combo") ||
      "";

    const nextRecord = {
      id: editingComboId || `combo-${Date.now()}`,
      type: "combo",
      title,
      hook: comboForm.hook.trim() || `Combo ${formatCurrency(comboPrice)}`,
      categoryId: category.id,
      categoryName: category.nombre,
      validity,
      detail,
      imageUrl: comboImageUrl,
      fontPreset: comboForm.fontPreset,
      customFontFamily: comboForm.customFontFamily,
      fontFamily,
      schedule,
      scheduleSummary: buildPromotionScheduleSummary(schedule),
      active: existingRecord?.active ?? true,
      deliveryAvailable: comboForm.deliveryAvailable === true,
      paymentCondition: comboForm.paymentCondition,
      items: comboItems.map((item) => ({ ...item })),
      comboPrice,
      baseTotal: comboBaseTotal,
      savingsAmount,
      pricingSummary,
      createdAt: existingRecord?.createdAt || new Date().toISOString(),
    };

    const persistedCombo = await persistRecords(
      existingRecord
        ? promotions.map((record) => (record.id === editingComboId ? nextRecord : record))
        : [nextRecord, ...promotions]
    );

    if (!persistedCombo?.records) {
      return;
    }

    resetComboComposer();
    setFeedback({
      type: persistedCombo.usedLocalFallback ? "error" : "success",
      text: persistedCombo.usedLocalFallback
        ? persistedCombo.error?.message ||
          "Guardamos el combo solo en este equipo. La imagen no llego a Supabase todavia."
        : existingRecord
          ? "Combo actualizado para esta sucursal."
          : "Combo guardado para esta sucursal.",
    });
  };

  const handleCreateDailyMenu = async (event) => {
    event.preventDefault();

    if (!canManagePromos) {
      notifyPromoPermissionDenied("modificar menus del dia");
      return;
    }

    const title = dailyMenuForm.title.trim();
    const detail = dailyMenuForm.detail.trim();
    const comboPrice = Number(dailyMenuForm.comboPrice || 0);
    const dailyMenuDate = buildLocalDateKey(new Date());

    if (!title) {
      setFeedback({ type: "error", text: "Ponle un nombre al menu del dia." });
      return;
    }

    if (!dailyMenuItems.length) {
      setFeedback({
        type: "error",
        text: "El menu del dia necesita al menos un plato o producto cargado.",
      });
      return;
    }

    if (comboPrice <= 0) {
      setFeedback({
        type: "error",
        text: "Carga un precio final valido para el menu del dia.",
      });
      return;
    }

    const fontFamily = getPromotionFontFamily(dailyMenuForm);
    const savingsAmount = Math.max(0, dailyMenuBaseTotal - comboPrice);
    const pricingSummary = savingsAmount
      ? `${formatCurrency(comboPrice)} \u00b7 Ahorras ${formatCurrency(savingsAmount)}`
      : `${formatCurrency(comboPrice)}`;
    const existingRecord = editingDailyMenuId
      ? promotions.find((record) => record.id === editingDailyMenuId)
      : null;
    const firstItem = dailyMenuItems[0] || null;
    const firstProduct = firstItem
      ? menuProducts.find((product) => product.id === firstItem.productId) || null
      : null;
    const firstCategory = firstProduct
      ? menuCategories.find((category) => category.id === firstProduct.categoria) || null
      : null;
    const dailyMenuImageUrl =
      dailyMenuImageDraftUrl ||
      dailyMenuForm.imageUrl ||
      resolveDraftImageFallback(currentBranch?.id, "dailyMenu") ||
      "";

    const nextRecord = {
      id: editingDailyMenuId || `daily-menu-${Date.now()}`,
      type: "combo",
      comboKind: "daily_menu",
      dailyMenuDate,
      title,
      hook: `Menu del dia ${formatCurrency(comboPrice)}`,
      categoryId: firstCategory?.id || "",
      categoryName: firstCategory?.nombre || "Menu del dia",
      validity: "Solo por hoy",
      detail,
      imageUrl: dailyMenuImageUrl,
      fontPreset: dailyMenuForm.fontPreset,
      customFontFamily: dailyMenuForm.customFontFamily,
      fontFamily,
      schedule: [],
      scheduleSummary: "Solo hoy",
      active: existingRecord?.active ?? true,
      deliveryAvailable: dailyMenuForm.deliveryAvailable === true,
      paymentCondition: dailyMenuForm.paymentCondition,
      items: dailyMenuItems.map((item) => ({ ...item })),
      comboPrice,
      baseTotal: dailyMenuBaseTotal,
      savingsAmount,
      pricingSummary,
      createdAt: existingRecord?.createdAt || new Date().toISOString(),
    };

    const persistedDailyMenu = await persistRecords(
      existingRecord
        ? promotions.map((record) =>
            record.id === editingDailyMenuId ? nextRecord : record
          )
        : [nextRecord, ...promotions]
    );

    if (!persistedDailyMenu?.records) {
      return;
    }

    resetDailyMenuComposer();
    setFeedback({
      type: persistedDailyMenu.usedLocalFallback ? "error" : "success",
      text: persistedDailyMenu.usedLocalFallback
        ? persistedDailyMenu.error?.message ||
          "Guardamos el menu del dia solo en este equipo. La imagen no llego a Supabase todavia."
        : existingRecord
          ? "Menu del dia actualizado para hoy."
          : "Menu del dia guardado para esta sucursal.",
    });
  };

  const handleCreatePromotionRule = async (event) => {
    event.preventDefault();

    if (!canManagePromos) {
      notifyPromoPermissionDenied("modificar promociones");
      return;
    }

    const title = promotionRuleForm.title.trim();
    const validity = promotionRuleForm.validity.trim();
    const detail = promotionRuleForm.detail.trim();
    const discountValue = Number(promotionRuleForm.discountValue || 0);
    const schedule = normalizePromotionSchedule(promotionRuleForm.schedule);

    if (!title) {
      setFeedback({ type: "error", text: "Ponle un nombre a la promocion." });
      return;
    }

    if (!selectedPromotionCategory?.id) {
      setFeedback({ type: "error", text: "Elige al menos una categoria base." });
      return;
    }

    if (
      promotionRuleForm.scopeType === "subcategory" &&
      !selectedPromotionSubcategory?.id
    ) {
      setFeedback({
        type: "error",
        text: "Selecciona la subcategoria sobre la que aplicara la promo.",
      });
      return;
    }

    if (promotionRuleForm.scopeType === "product" && !selectedPromotionProduct?.id) {
      setFeedback({
        type: "error",
        text: "Selecciona el producto puntual para la promocion.",
      });
      return;
    }

    if (discountValue <= 0) {
      setFeedback({
        type: "error",
        text: "Carga el porcentaje o monto de descuento que quieras aplicar.",
      });
      return;
    }

    const fontFamily = getPromotionFontFamily(promotionRuleForm);
    const pricingSummary = formatDiscountSummary(
      promotionRuleForm.discountType,
      discountValue
    );
    const targetLabel = buildPromotionTargetSummary({
      scopeType: promotionRuleForm.scopeType,
      categoryName: selectedPromotionCategory?.nombre || "",
      subcategoryName: selectedPromotionSubcategory?.nombre || "",
      productName: selectedPromotionProduct?.nombre || "",
    });

    const existingRecord = editingPromotionId
      ? promotions.find((record) => record.id === editingPromotionId)
      : null;
    const promotionImageUrl =
      promotionImageDraftUrl ||
      promotionRuleForm.imageUrl ||
      resolveDraftImageFallback(currentBranch?.id, "promotion") ||
      "";

    const nextRecord = {
      id: editingPromotionId || `promotion-${Date.now()}`,
      type: "promotion",
      title,
      hook: promotionRuleForm.hook.trim() || pricingSummary,
      categoryId: selectedPromotionCategory.id,
      categoryName: selectedPromotionCategory.nombre,
      subcategoryId: selectedPromotionSubcategory?.id || "",
      subcategoryName: selectedPromotionSubcategory?.nombre || "",
      productId: selectedPromotionProduct?.id || "",
      productName: selectedPromotionProduct?.nombre || "",
      scopeType: promotionRuleForm.scopeType,
      targetLabel,
      discountType: promotionRuleForm.discountType,
      discountValue,
      pricingSummary,
      validity,
      detail: detail || `${pricingSummary} en ${targetLabel}`,
      imageUrl: promotionImageUrl,
      fontPreset: promotionRuleForm.fontPreset,
      customFontFamily: promotionRuleForm.customFontFamily,
      fontFamily,
      schedule,
      scheduleSummary: buildPromotionScheduleSummary(schedule),
      active: existingRecord?.active ?? true,
      deliveryAvailable: promotionRuleForm.deliveryAvailable === true,
      paymentCondition: promotionRuleForm.paymentCondition,
      createdAt: existingRecord?.createdAt || new Date().toISOString(),
    };

    const persistedPromotion = await persistRecords(
      existingRecord
        ? promotions.map((record) =>
            record.id === editingPromotionId ? nextRecord : record
          )
        : [nextRecord, ...promotions]
    );

    if (!persistedPromotion?.records) {
      return;
    }

    resetPromotionComposer();
    setFeedback({
      type: persistedPromotion.usedLocalFallback ? "error" : "success",
      text: persistedPromotion.usedLocalFallback
        ? persistedPromotion.error?.message ||
          "Guardamos la promocion solo en este equipo. La imagen no llego a Supabase todavia."
        : existingRecord
          ? "Promocion actualizada para esta sucursal."
          : "Promocion guardada para esta sucursal.",
    });
  };

  const toggleRecordActive = async (recordId) => {
    if (!canManagePromos) {
      notifyPromoPermissionDenied("activar o pausar promociones");
      return;
    }

    const nextRecords = promotions.map((record) =>
      record.id === recordId ? { ...record, active: record.active === false } : record
    );
    const result = await persistRecords(nextRecords);

    if (result?.usedLocalFallback) {
      setFeedback({
        type: "error",
        text:
          result.error?.message ||
          "Actualizamos el estado solo en este equipo. La copia remota sigue pendiente.",
      });
    }
  };

  const toggleRecordDelivery = async (recordId) => {
    if (!canManagePromos) {
      notifyPromoPermissionDenied("cambiar la disponibilidad en delivery");
      return;
    }

    const nextRecords = promotions.map((record) =>
      record.id === recordId
        ? { ...record, deliveryAvailable: record.deliveryAvailable !== true }
        : record
    );
    const result = await persistRecords(nextRecords);

    if (result?.usedLocalFallback) {
      setFeedback({
        type: "error",
        text:
          result.error?.message ||
          "Actualizamos delivery solo en este equipo. La copia remota sigue pendiente.",
      });
    }
  };

  const deleteRecord = async (recordId) => {
    if (!canManagePromos) {
      notifyPromoPermissionDenied("eliminar promociones");
      return;
    }

    const record = promotions.find((item) => item.id === recordId);

    if (!record) {
      return;
    }

    const confirmed = window.confirm(
      `Confirma que deseas eliminar "${record.title}".`
    );

    if (!confirmed) {
      return;
    }

    const persistedDeletion = await persistRecords(
      promotions.filter((recordItem) => recordItem.id !== recordId)
    );

    if (!persistedDeletion?.records) {
      return;
    }

    if (editingComboId === recordId) {
      resetComboComposer();
    }
    if (editingDailyMenuId === recordId) {
      resetDailyMenuComposer();
    }
    if (editingPromotionId === recordId) {
      resetPromotionComposer();
    }

    if (persistedDeletion.usedLocalFallback) {
      setFeedback({
        type: "error",
        text:
          persistedDeletion.error?.message ||
          "Eliminamos el registro solo en este equipo. Falta sincronizar la baja remota.",
      });
    }
  };

  return (
    <div style={styles.page}>
      <section style={styles.hero}>
        <div>
          <span style={styles.heroEyebrow}>Promos, combos y menu del dia</span>
          <h1 style={styles.heroTitle}>Campanas comerciales de la sucursal</h1>
          <p style={styles.heroText}>
            Separamos combos, promociones y menus del dia para que caja, QR y operacion
            hablen el mismo idioma. Los combos y menus del dia tienen precio final propio.
            Las promociones aplican reglas por categoria, subcategoria o producto.
          </p>
        </div>

        <div style={styles.heroStats}>
          <article style={styles.statCard}>
            <span style={styles.statLabel}>Combos activos</span>
            <strong style={styles.statValue}>{activeComboCount}</strong>
            <span style={styles.statMeta}>{comboRecords.length} configurados</span>
          </article>
          <article style={styles.statCard}>
            <span style={styles.statLabel}>Menus del dia activos</span>
            <strong style={styles.statValue}>{activeDailyMenuCount}</strong>
            <span style={styles.statMeta}>{dailyMenuRecords.length} cargados</span>
          </article>
          <article style={styles.statCard}>
            <span style={styles.statLabel}>Promociones activas</span>
            <strong style={styles.statValue}>{activePromotionCount}</strong>
            <span style={styles.statMeta}>{promotionRecords.length} configuradas</span>
          </article>
        </div>
      </section>

      {menuError ? <div style={styles.errorBanner}>{menuError}</div> : null}
      {feedback.text ? (
        <div
          style={{
            ...styles.feedbackBanner,
            ...(feedback.type === "error" ? styles.feedbackError : styles.feedbackSuccess),
          }}
        >
          {feedback.text}
        </div>
      ) : null}
      {draftSaveMode === "compact" ? (
        <div style={{ ...styles.feedbackBanner, ...styles.feedbackWarning }}>
          Guardamos el borrador de promos en modo liviano. Si subiste imagenes muy pesadas,
          puede que no se conserven hasta publicar la promo.
        </div>
      ) : null}
      {draftSaveMode === "failed" ? (
        <div style={{ ...styles.feedbackBanner, ...styles.feedbackError }}>
          Este navegador ya no tiene espacio para guardar el borrador local de promos. Puedes
          seguir editando, pero conviene publicar o reducir imagenes pesadas.
        </div>
      ) : null}

      <section style={styles.tabsRow}>
        <button
          type="button"
          style={{
            ...styles.tabButton,
            ...(activeTab === "combos" ? styles.tabButtonActive : {}),
          }}
          onClick={() => setActiveTab("combos")}
        >
          Combos
        </button>
        <button
          type="button"
          style={{
            ...styles.tabButton,
            ...(activeTab === "promotions" ? styles.tabButtonActive : {}),
          }}
          onClick={() => setActiveTab("promotions")}
        >
          Promociones
        </button>
        <button
          type="button"
          style={{
            ...styles.tabButton,
            ...(activeTab === "daily_menus" ? styles.tabButtonActive : {}),
          }}
          onClick={() => setActiveTab("daily_menus")}
        >
          Menu del dia
        </button>
      </section>

      {activeTab === "combos" ? (
        <div style={styles.layoutGrid}>
          <section style={styles.panel}>
            <div style={styles.panelHeader}>
              <div>
                <h2 style={styles.panelTitle}>
                  {editingComboId ? "Editar combo" : "Nuevo combo"}
                </h2>
                <p style={styles.panelText}>
                  Arma un paquete fijo con precio final propio. El sistema guarda tambien
                  el valor base para entender cuanto estas descontando.
                </p>
              </div>
              <span style={styles.panelPill}>
                {editingComboId ? "Editando" : `${activeComboCount} activos`}
              </span>
            </div>

            {!canManagePromos ? (
              <div style={styles.readOnlyNotice}>
                Estás viendo este módulo en solo lectura. Para crear o editar combos necesitás
                acceso de admin general o permiso de gestión de menú.
              </div>
            ) : null}

            <form onSubmit={handleCreateCombo} style={styles.formGrid}>
              <fieldset style={styles.formFieldset} disabled={!canManagePromos}>
              <label style={styles.field}>
                <span style={styles.label}>Nombre del combo</span>
                <input
                  type="text"
                  value={comboForm.title}
                  onChange={(event) =>
                    setComboForm((prev) => ({ ...prev, title: event.target.value }))
                  }
                  style={styles.input}
                  placeholder="Desayuno clasico"
                />
              </label>

              <label style={styles.field}>
                <span style={styles.label}>Gancho visible</span>
                <input
                  type="text"
                  value={comboForm.hook}
                  onChange={(event) =>
                    setComboForm((prev) => ({ ...prev, hook: event.target.value }))
                  }
                  style={styles.input}
                  placeholder="$4.000 o Combo estrella"
                />
              </label>

              <label style={styles.field}>
                <span style={styles.label}>Categoria base</span>
                <select
                  value={comboForm.categoryId}
                  onChange={(event) =>
                    setComboForm((prev) => ({ ...prev, categoryId: event.target.value }))
                  }
                  style={styles.input}
                >
                  <option value="">Selecciona una categoria</option>
                  {menuCategories.map((category) => (
                    <option key={category.id} value={category.id}>
                      {category.nombre}
                    </option>
                  ))}
                </select>
              </label>

              <label style={styles.field}>
                <span style={styles.label}>Precio final del combo</span>
                <input
                  type="number"
                  min="0"
                  step="1"
                  value={comboForm.comboPrice}
                  onChange={(event) =>
                    setComboForm((prev) => ({ ...prev, comboPrice: event.target.value }))
                  }
                  style={styles.input}
                  placeholder="4000"
                />
              </label>

              <label style={styles.field}>
                <span style={styles.label}>Leyenda visible</span>
                <input
                  type="text"
                  value={comboForm.validity}
                  onChange={(event) =>
                    setComboForm((prev) => ({ ...prev, validity: event.target.value }))
                  }
                  style={styles.input}
                  placeholder="Lunes a viernes de 8 a 12"
                />
              </label>

              <label style={styles.field}>
                <span style={styles.label}>Tipografia</span>
                <select
                  value={comboForm.fontPreset}
                  onChange={(event) =>
                    setComboForm((prev) => ({ ...prev, fontPreset: event.target.value }))
                  }
                  style={styles.input}
                >
                  {PROMOTION_FONT_OPTIONS.map((option) => (
                    <option key={option.id} value={option.id}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>

              {comboForm.fontPreset === "custom" ? (
                <label style={styles.field}>
                  <span style={styles.label}>Font family custom</span>
                  <input
                    type="text"
                    value={comboForm.customFontFamily}
                    onChange={(event) =>
                      setComboForm((prev) => ({
                        ...prev,
                        customFontFamily: event.target.value,
                      }))
                    }
                    style={styles.input}
                    placeholder='"Bebas Neue", Arial, sans-serif'
                  />
                </label>
              ) : null}

              <div style={styles.scheduleCard}>
                <span style={styles.label}>Dias y horarios</span>
                <strong style={styles.scheduleTitle}>
                  {buildPromotionScheduleSummary(comboForm.schedule)}
                </strong>
                <span style={styles.scheduleHint}>
                  Configuralo en una ventana aparte para no ensanchar el formulario.
                </span>
                <button
                  type="button"
                  style={styles.secondaryButton}
                  onClick={() => openScheduleModal("combo")}
                >
                  Configurar dias y horarios
                </button>
              </div>

              <label style={styles.deliveryOption}>
                <input
                  type="checkbox"
                  checked={comboForm.deliveryAvailable === true}
                  onChange={(event) =>
                    setComboForm((prev) => ({
                      ...prev,
                      deliveryAvailable: event.target.checked,
                    }))
                  }
                  style={styles.deliveryCheckbox}
                />
                <span style={styles.deliveryOptionText}>
                  <strong>Disponible en Delivery y We! pedidos</strong>
                  <small>El combo aparecerá en la carta y su precio se aplicará al pedido.</small>
                </span>
              </label>

              <label style={styles.field}>
                <span style={styles.label}>Precio de la oferta valido con</span>
                <select style={styles.input} value={comboForm.paymentCondition}
                  onChange={(event) => setComboForm((prev) => ({ ...prev, paymentCondition: event.target.value }))}>
                  <option value="any">Todos los medios de pago</option>
                  <option value="cash_only">Solo efectivo</option>
                </select>
              </label>

              <label style={styles.field}>
                <span style={styles.label}>Imagen del combo</span>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleComboImageSelected}
                  style={styles.fileInput}
                />
                <span style={styles.fileStatus}>
                  {comboImageProcessing
                    ? "Preparando la imagen del combo..."
                    : resolvedComboPreviewImageUrl
                      ? `Imagen cargada${
                          comboImageDraftName ? `: ${comboImageDraftName}` : ""
                        }. Queda lista para guardar con el combo.`
                      : "Todavia no cargaste una imagen para este combo."}
                </span>
              </label>

              <div style={styles.previewCard}>
                <span style={styles.label}>Vista previa</span>
                <strong
                  style={{
                    ...styles.previewTitle,
                    fontFamily: getPromotionFontFamily(comboForm),
                  }}
                >
                  {comboForm.title.trim() || "Combo de la casa"}
                </strong>
                {resolvedComboPreviewImageUrl ? (
                  <img
                    src={resolvedComboPreviewImageUrl}
                    alt="Vista previa del combo"
                    style={styles.previewImage}
                  />
                ) : null}
              </div>

              <div style={styles.composerPanel}>
                <div style={styles.composerHeader}>
                  <strong style={styles.composerTitle}>Productos del combo</strong>
                  <span style={styles.composerHint}>
                    Puedes combinar productos de toda la carta. La categoria base solo
                    define donde se muestra el combo.
                  </span>
                </div>

                <div style={styles.inlineGrid}>
                  <label style={styles.field}>
                    <span style={styles.label}>Buscar producto</span>
                    <input
                      type="text"
                      value={comboSearch}
                      onChange={(event) => setComboSearch(event.target.value)}
                      style={styles.input}
                      placeholder="Cafe, medialuna, tostado..."
                    />
                  </label>

                  <label style={styles.field}>
                    <span style={styles.label}>Producto</span>
                    <select
                      value={comboProductId}
                      onChange={(event) => setComboProductId(event.target.value)}
                      style={styles.input}
                    >
                      <option value="">Selecciona un producto</option>
                      {filteredComboProducts.map((product) => (
                        <option key={product.id} value={product.id}>
                          {product.nombre}
                        </option>
                      ))}
                    </select>
                  </label>

                  <label style={styles.field}>
                    <span style={styles.label}>Cantidad</span>
                    <input
                      type="number"
                      min="1"
                      step="1"
                      value={comboProductQuantity}
                      onChange={(event) => setComboProductQuantity(event.target.value)}
                      style={styles.input}
                    />
                  </label>
                </div>

                {(comboSearch.trim() || comboForm.categoryId) && (
                  <div style={styles.searchPanel}>
                    <div style={styles.searchHeader}>
                      <strong>Resultados del menu</strong>
                      <span>{filteredComboProducts.length} encontrados</span>
                    </div>

                    {filteredComboProducts.length > 0 ? (
                      <div style={styles.searchGrid}>
                        {filteredComboProducts.slice(0, 10).map((product) => (
                          <button
                            key={product.id}
                            type="button"
                            style={{
                              ...styles.searchCard,
                              ...(comboProductId === product.id ? styles.searchCardActive : {}),
                            }}
                            onClick={() => setComboProductId(product.id)}
                          >
                            <strong style={styles.searchName}>{product.nombre}</strong>
                            <span style={styles.searchMeta}>
                              {product.subcategoria || "Sin subcategoria"}
                            </span>
                            <span style={styles.searchPrice}>
                              {formatCurrency(resolveProductUnitPrice(product))}
                            </span>
                          </button>
                        ))}
                      </div>
                    ) : (
                      <div style={styles.emptyState}>No hay coincidencias en esa busqueda.</div>
                    )}
                  </div>
                )}

                {selectedComboProduct ? (
                  <div style={styles.selectionSummary}>
                    Seleccionado: <strong>{selectedComboProduct.nombre}</strong>
                  </div>
                ) : null}

                <div style={styles.actionsInline}>
                  <button type="button" style={styles.primaryButton} onClick={addComboItem}>
                    Agregar al combo
                  </button>
                </div>

                {comboItems.length > 0 ? (
                  <div style={styles.listStack}>
                    {comboItems.map((item) => (
                      <div key={item.productId} style={styles.listCard}>
                        <div>
                          <strong style={styles.listTitle}>
                            {item.quantity}x {item.name}
                          </strong>
                          <div style={styles.listMeta}>
                            {formatCurrency(item.unitPrice)} por unidad
                            {item.subcategoryName ? ` · ${item.subcategoryName}` : ""}
                          </div>
                        </div>
                        <button
                          type="button"
                          style={styles.secondaryButton}
                          onClick={() => removeComboItem(item.productId)}
                        >
                          Quitar
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div style={styles.emptyState}>Todavia no agregaste productos al combo.</div>
                )}

                <div style={styles.priceSummaryCard}>
                  <div>
                    <span style={styles.label}>Precio base</span>
                    <strong style={styles.summaryValue}>{formatCurrency(comboBaseTotal)}</strong>
                  </div>
                  <div>
                    <span style={styles.label}>Precio combo</span>
                    <strong style={styles.summaryValue}>
                      {comboPriceValue > 0 ? formatCurrency(comboPriceValue) : "--"}
                    </strong>
                  </div>
                  <div>
                    <span style={styles.label}>Ahorro</span>
                    <strong style={styles.summaryValue}>
                      {comboSavings > 0 ? formatCurrency(comboSavings) : "--"}
                    </strong>
                  </div>
                </div>
              </div>

              <label style={{ ...styles.field, gridColumn: "1 / -1" }}>
                <span style={styles.label}>Detalle</span>
                <textarea
                  value={comboForm.detail}
                  onChange={(event) =>
                    setComboForm((prev) => ({ ...prev, detail: event.target.value }))
                  }
                  style={styles.textarea}
                  placeholder="Describe que incluye, condiciones o como lo comunicaras."
                />
              </label>

              <div style={styles.formActions}>
                <button type="button" style={styles.secondaryButton} onClick={resetComboComposer}>
                  {editingComboId ? "Cancelar edicion" : "Limpiar"}
                </button>
                <button
                  type="submit"
                  style={styles.primaryButton}
                  disabled={comboImageProcessing}
                >
                  {editingComboId ? "Guardar cambios" : "Guardar combo"}
                </button>
              </div>
              </fieldset>
            </form>
          </section>

          <section style={styles.panel}>
            <div style={styles.panelHeader}>
              <div>
                <h2 style={styles.panelTitle}>Combos creados</h2>
                <p style={styles.panelText}>
                  Aqui queda claro que incluye cada paquete y que ahorro real genera.
                </p>
              </div>
            </div>

            {comboRecords.length > 0 ? (
              <div style={styles.recordsGrid}>
                {comboRecords.map((record) => (
                  <article key={record.id} style={styles.recordCard}>
                    {record.imageUrl ? (
                      <img
                        src={record.imageUrl}
                        alt={`Combo ${record.title}`}
                        style={styles.recordImage}
                      />
                    ) : null}

                    <div style={styles.recordHeader}>
                      <div>
                        <span
                          style={{
                            ...styles.recordHook,
                            fontFamily: record.fontFamily || undefined,
                          }}
                        >
                          {record.hook || "Combo"}{record.paymentCondition === "cash_only" ? " · Solo efectivo" : ""}
                        </span>
                        <h3 style={styles.recordTitle}>{record.title}</h3>
                      </div>
                      <span
                        style={{
                          ...styles.statusBadge,
                          ...(record.active === false ? styles.statusMuted : styles.statusLive),
                        }}
                      >
                        {record.active === false ? "Pausado" : "Activo"}
                      </span>
                    </div>

                    <div style={styles.metricRow}>
                      <span style={styles.metricChip}>Base {formatCurrency(record.baseTotal)}</span>
                      <span style={styles.metricChip}>
                        Combo {formatCurrency(record.comboPrice)}
                      </span>
                      {Number(record.savingsAmount || 0) > 0 ? (
                        <span style={styles.metricChip}>
                          Ahorro {formatCurrency(record.savingsAmount)}
                        </span>
                      ) : null}
                      <span
                        style={{
                          ...styles.metricChip,
                          ...(record.deliveryAvailable === true
                            ? styles.deliveryMetricLive
                            : styles.deliveryMetricMuted),
                        }}
                      >
                        {record.deliveryAvailable === true
                          ? "Disponible en Delivery"
                          : "Fuera de Delivery"}
                      </span>
                    </div>

                    <div style={styles.detailBlock}>
                      <span style={styles.detailLabel}>Categoria</span>
                      <div style={styles.detailText}>{record.categoryName || "Sin categoria"}</div>
                    </div>

                    <div style={styles.detailBlock}>
                      <span style={styles.detailLabel}>Dias y horarios</span>
                      <div style={styles.detailText}>
                        {record.scheduleSummary ||
                          buildPromotionScheduleSummary(record.schedule)}
                      </div>
                    </div>

                    {Array.isArray(record.items) && record.items.length > 0 ? (
                      <div style={styles.detailBlock}>
                        <span style={styles.detailLabel}>Incluye</span>
                        <div style={styles.chipWrap}>
                          {record.items.map((item) => (
                            <span key={`${record.id}-${item.productId}`} style={styles.comboChip}>
                              {item.quantity}x {item.name}
                            </span>
                          ))}
                        </div>
                      </div>
                    ) : null}

                    <div style={styles.detailBlock}>
                      <span style={styles.detailLabel}>Detalle</span>
                      <div style={styles.detailText}>
                        {record.detail || "Sin detalle adicional cargado todavia."}
                      </div>
                    </div>

                    <div style={styles.actionRow}>
                      {canManagePromos ? (
                        <>
                          <button
                            type="button"
                            style={styles.secondaryButton}
                            onClick={() => startEditingCombo(record)}
                          >
                            Editar
                          </button>
                          <button
                            type="button"
                            style={styles.primaryButton}
                            onClick={() => toggleRecordActive(record.id)}
                          >
                            {record.active === false ? "Activar" : "Pausar"}
                          </button>
                          <button
                            type="button"
                            style={styles.secondaryButton}
                            onClick={() => toggleRecordDelivery(record.id)}
                          >
                            {record.deliveryAvailable === true
                              ? "Quitar de Delivery"
                              : "Publicar en Delivery"}
                          </button>
                          <button
                            type="button"
                            style={styles.secondaryButton}
                            onClick={() => deleteRecord(record.id)}
                          >
                            Eliminar
                          </button>
                        </>
                      ) : (
                        <span style={styles.readOnlyInline}>Solo lectura</span>
                      )}
                    </div>
                  </article>
                ))}
              </div>
            ) : (
              <div style={styles.emptyState}>
                Todavia no hay combos creados para esta sucursal.
              </div>
            )}
          </section>
        </div>
      ) : activeTab === "daily_menus" ? (
        <div style={styles.layoutGrid}>
          <section style={styles.panel}>
            <div style={styles.panelHeader}>
              <div>
                <h2 style={styles.panelTitle}>
                  {editingDailyMenuId ? "Editar menu del dia" : "Nuevo menu del dia"}
                </h2>
                <p style={styles.panelText}>
                  Tomas productos ya cargados en la carta, les pones un precio final unico y
                  queda activo solo durante la fecha actual.
                </p>
              </div>
              <span style={styles.panelPill}>
                {editingDailyMenuId ? "Editando" : `${activeDailyMenuCount} activos hoy`}
              </span>
            </div>

            {!canManagePromos ? (
              <div style={styles.readOnlyNotice}>
                Estás viendo este módulo en solo lectura. Para crear o editar menus del dia
                necesitás acceso de admin general o permiso de gestión de menú.
              </div>
            ) : null}

            <form onSubmit={handleCreateDailyMenu} style={styles.formGrid}>
              <fieldset style={styles.formFieldset} disabled={!canManagePromos}>
                <label style={styles.field}>
                  <span style={styles.label}>Nombre del menu del dia</span>
                  <input
                    type="text"
                    value={dailyMenuForm.title}
                    onChange={(event) =>
                      setDailyMenuForm((prev) => ({ ...prev, title: event.target.value }))
                    }
                    style={styles.input}
                    placeholder="Menu ejecutivo"
                  />
                </label>

                <label style={styles.field}>
                  <span style={styles.label}>Precio final</span>
                  <input
                    type="number"
                    min="0"
                    step="1"
                    value={dailyMenuForm.comboPrice}
                    onChange={(event) =>
                      setDailyMenuForm((prev) => ({
                        ...prev,
                        comboPrice: event.target.value,
                      }))
                    }
                    style={styles.input}
                    placeholder="12500"
                  />
                </label>

                <label style={styles.field}>
                  <span style={styles.label}>Tipografia</span>
                  <select
                    value={dailyMenuForm.fontPreset}
                    onChange={(event) =>
                      setDailyMenuForm((prev) => ({
                        ...prev,
                        fontPreset: event.target.value,
                      }))
                    }
                    style={styles.input}
                  >
                    {PROMOTION_FONT_OPTIONS.map((option) => (
                      <option key={option.id} value={option.id}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </label>

                <div style={styles.scheduleCard}>
                  <span style={styles.label}>Vigencia</span>
                  <strong style={styles.scheduleTitle}>Solo hoy</strong>
                  <span style={styles.scheduleHint}>
                    Se publica con fecha del dia actual y desaparece sola del Menu QR al
                    cambiar la fecha.
                  </span>
                </div>

                <label style={styles.deliveryOption}>
                  <input
                    type="checkbox"
                    checked={dailyMenuForm.deliveryAvailable === true}
                    onChange={(event) =>
                      setDailyMenuForm((prev) => ({
                        ...prev,
                        deliveryAvailable: event.target.checked,
                      }))
                    }
                    style={styles.deliveryCheckbox}
                  />
                  <span style={styles.deliveryOptionText}>
                    <strong>Disponible en Delivery y We! pedidos</strong>
                    <small>Se mostrará solamente hoy y desaparecerá cuando cambie la fecha.</small>
                  </span>
                </label>

                <label style={styles.field}>
                  <span style={styles.label}>Precio de la oferta valido con</span>
                  <select style={styles.input} value={dailyMenuForm.paymentCondition}
                    onChange={(event) => setDailyMenuForm((prev) => ({ ...prev, paymentCondition: event.target.value }))}>
                    <option value="any">Todos los medios de pago</option>
                    <option value="cash_only">Solo efectivo</option>
                  </select>
                </label>

                {dailyMenuForm.fontPreset === "custom" ? (
                  <label style={styles.field}>
                    <span style={styles.label}>Font family custom</span>
                    <input
                      type="text"
                      value={dailyMenuForm.customFontFamily}
                      onChange={(event) =>
                        setDailyMenuForm((prev) => ({
                          ...prev,
                          customFontFamily: event.target.value,
                        }))
                      }
                      style={styles.input}
                      placeholder='"Bebas Neue", Arial, sans-serif'
                    />
                  </label>
                ) : null}

                <label style={styles.field}>
                  <span style={styles.label}>Imagen del menu del dia</span>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleDailyMenuImageSelected}
                    style={styles.fileInput}
                  />
                  <span style={styles.fileStatus}>
                    {dailyMenuImageProcessing
                      ? "Preparando la imagen del menu del dia..."
                      : resolvedDailyMenuPreviewImageUrl
                        ? `Imagen cargada${
                            dailyMenuImageDraftName ? `: ${dailyMenuImageDraftName}` : ""
                          }. Queda lista para guardar con el menu del dia.`
                        : "Todavia no cargaste una imagen para este menu del dia."}
                  </span>
                </label>

                <div style={styles.previewCard}>
                  <span style={styles.label}>Vista previa</span>
                  <strong
                    style={{
                      ...styles.previewTitle,
                      fontFamily: getPromotionFontFamily(dailyMenuForm),
                    }}
                  >
                    {dailyMenuForm.title.trim() || "Menu del dia"}
                  </strong>
                  <span style={styles.previewMeta}>
                    {dailyMenuPriceValue > 0
                      ? `Solo hoy ${formatCurrency(dailyMenuPriceValue)}`
                      : "Carga el precio final"}
                  </span>
                  {resolvedDailyMenuPreviewImageUrl ? (
                    <img
                      src={resolvedDailyMenuPreviewImageUrl}
                      alt="Vista previa del menu del dia"
                      style={styles.previewImage}
                    />
                  ) : null}
                </div>

                <div style={styles.composerPanel}>
                  <div style={styles.composerHeader}>
                    <strong style={styles.composerTitle}>Productos del menu del dia</strong>
                    <span style={styles.composerHint}>
                      Puedes mezclar platos, bebidas, postres o cualquier item ya creado en
                      la carta.
                    </span>
                  </div>

                  <div style={styles.inlineGrid}>
                    <label style={styles.field}>
                      <span style={styles.label}>Buscar producto</span>
                      <input
                        type="text"
                        value={dailyMenuSearch}
                        onChange={(event) => setDailyMenuSearch(event.target.value)}
                        style={styles.input}
                        placeholder="Milanesa, gaseosa, postre..."
                      />
                    </label>

                    <label style={styles.field}>
                      <span style={styles.label}>Producto</span>
                      <select
                        value={dailyMenuProductId}
                        onChange={(event) => setDailyMenuProductId(event.target.value)}
                        style={styles.input}
                      >
                        <option value="">Selecciona un producto</option>
                        {filteredDailyMenuProducts.map((product) => (
                          <option key={product.id} value={product.id}>
                            {product.nombre}
                          </option>
                        ))}
                      </select>
                    </label>

                    <label style={styles.field}>
                      <span style={styles.label}>Cantidad</span>
                      <input
                        type="number"
                        min="1"
                        step="1"
                        value={dailyMenuProductQuantity}
                        onChange={(event) => setDailyMenuProductQuantity(event.target.value)}
                        style={styles.input}
                      />
                    </label>
                  </div>

                  {dailyMenuSearch.trim() ? (
                    <div style={styles.searchPanel}>
                      <div style={styles.searchHeader}>
                        <strong>Resultados del menu</strong>
                        <span>{filteredDailyMenuProducts.length} encontrados</span>
                      </div>

                      {filteredDailyMenuProducts.length > 0 ? (
                        <div style={styles.searchGrid}>
                          {filteredDailyMenuProducts.slice(0, 10).map((product) => (
                            <button
                              key={product.id}
                              type="button"
                              style={{
                                ...styles.searchCard,
                                ...(dailyMenuProductId === product.id
                                  ? styles.searchCardActive
                                  : {}),
                              }}
                              onClick={() => setDailyMenuProductId(product.id)}
                            >
                              <strong style={styles.searchName}>{product.nombre}</strong>
                              <span style={styles.searchMeta}>
                                {product.subcategoria || "Sin subcategoria"}
                              </span>
                              <span style={styles.searchPrice}>
                                {formatCurrency(resolveProductUnitPrice(product))}
                              </span>
                            </button>
                          ))}
                        </div>
                      ) : (
                        <div style={styles.emptyState}>No hay coincidencias en esa busqueda.</div>
                      )}
                    </div>
                  ) : null}

                  {selectedDailyMenuProduct ? (
                    <div style={styles.selectionSummary}>
                      Seleccionado: <strong>{selectedDailyMenuProduct.nombre}</strong>
                    </div>
                  ) : null}

                  <div style={styles.actionsInline}>
                    <button
                      type="button"
                      style={styles.primaryButton}
                      onClick={addDailyMenuItem}
                    >
                      Agregar al menu del dia
                    </button>
                  </div>

                  {dailyMenuItems.length > 0 ? (
                    <div style={styles.listStack}>
                      {dailyMenuItems.map((item) => (
                        <div key={item.productId} style={styles.listCard}>
                          <div>
                            <strong style={styles.listTitle}>
                              {item.quantity}x {item.name}
                            </strong>
                            <div style={styles.listMeta}>
                              {formatCurrency(item.unitPrice)} por unidad
                              {item.subcategoryName ? ` \u00b7 ${item.subcategoryName}` : ""}
                            </div>
                          </div>
                          <button
                            type="button"
                            style={styles.secondaryButton}
                            onClick={() => removeDailyMenuItem(item.productId)}
                          >
                            Quitar
                          </button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div style={styles.emptyState}>
                      Todavia no agregaste platos al menu del dia.
                    </div>
                  )}

                  <div style={styles.priceSummaryCard}>
                    <div>
                      <span style={styles.label}>Precio base</span>
                      <strong style={styles.summaryValue}>
                        {formatCurrency(dailyMenuBaseTotal)}
                      </strong>
                    </div>
                    <div>
                      <span style={styles.label}>Precio menu del dia</span>
                      <strong style={styles.summaryValue}>
                        {dailyMenuPriceValue > 0
                          ? formatCurrency(dailyMenuPriceValue)
                          : "--"}
                      </strong>
                    </div>
                    <div>
                      <span style={styles.label}>Ahorro</span>
                      <strong style={styles.summaryValue}>
                        {dailyMenuSavings > 0 ? formatCurrency(dailyMenuSavings) : "--"}
                      </strong>
                    </div>
                  </div>
                </div>

                <label style={{ ...styles.field, gridColumn: "1 / -1" }}>
                  <span style={styles.label}>Descripcion</span>
                  <textarea
                    value={dailyMenuForm.detail}
                    onChange={(event) =>
                      setDailyMenuForm((prev) => ({ ...prev, detail: event.target.value }))
                    }
                    style={styles.textarea}
                    placeholder="Cuenta que incluye, si trae bebida, postre o condiciones especiales."
                  />
                </label>

                <div style={styles.formActions}>
                  <button
                    type="button"
                    style={styles.secondaryButton}
                    onClick={resetDailyMenuComposer}
                  >
                    {editingDailyMenuId ? "Cancelar edicion" : "Limpiar"}
                  </button>
                  <button
                    type="submit"
                    style={styles.primaryButton}
                    disabled={dailyMenuImageProcessing}
                  >
                    {editingDailyMenuId ? "Guardar cambios" : "Guardar menu del dia"}
                  </button>
                </div>
              </fieldset>
            </form>
          </section>

          <section style={styles.panel}>
            <div style={styles.panelHeader}>
              <div>
                <h2 style={styles.panelTitle}>Menus del dia cargados</h2>
                <p style={styles.panelText}>
                  Quedan listados con sus platos, precio final y ahorro para que el QR los
                  muestre junto a promos y combos.
                </p>
              </div>
            </div>

            {dailyMenuRecords.length > 0 ? (
              <div style={styles.recordsGrid}>
                {dailyMenuRecords.map((record) => (
                  <article key={record.id} style={styles.recordCard}>
                    {record.imageUrl ? (
                      <img
                        src={record.imageUrl}
                        alt={`Menu del dia ${record.title}`}
                        style={styles.recordImage}
                      />
                    ) : null}

                    <div style={styles.recordHeader}>
                      <div>
                        <span
                          style={{
                            ...styles.recordHook,
                            fontFamily: record.fontFamily || undefined,
                          }}
                        >
                          {record.hook || "Menu del dia"}{record.paymentCondition === "cash_only" ? " · Solo efectivo" : ""}
                        </span>
                        <h3 style={styles.recordTitle}>{record.title}</h3>
                      </div>
                      <span
                        style={{
                          ...styles.statusBadge,
                          ...(record.active === false ? styles.statusMuted : styles.statusLive),
                        }}
                      >
                        {record.active === false
                          ? "Pausado"
                          : String(record.dailyMenuDate || "").trim() === currentLocalDateKey
                            ? "Activo hoy"
                            : "Historico"}
                      </span>
                    </div>

                    <div style={styles.metricRow}>
                      <span style={styles.metricChip}>
                        Base {formatCurrency(record.baseTotal)}
                      </span>
                      <span style={styles.metricChip}>
                        Menu {formatCurrency(record.comboPrice)}
                      </span>
                      {Number(record.savingsAmount || 0) > 0 ? (
                        <span style={styles.metricChip}>
                          Ahorro {formatCurrency(record.savingsAmount)}
                        </span>
                      ) : null}
                      <span
                        style={{
                          ...styles.metricChip,
                          ...(record.deliveryAvailable === true
                            ? styles.deliveryMetricLive
                            : styles.deliveryMetricMuted),
                        }}
                      >
                        {record.deliveryAvailable === true
                          ? "Disponible en Delivery"
                          : "Fuera de Delivery"}
                      </span>
                    </div>

                    <div style={styles.detailBlock}>
                      <span style={styles.detailLabel}>Fecha</span>
                      <div style={styles.detailText}>
                        {record.dailyMenuDate || "Solo hoy"}
                      </div>
                    </div>

                    {Array.isArray(record.items) && record.items.length > 0 ? (
                      <div style={styles.detailBlock}>
                        <span style={styles.detailLabel}>Incluye</span>
                        <div style={styles.chipWrap}>
                          {record.items.map((item) => (
                            <span key={`${record.id}-${item.productId}`} style={styles.comboChip}>
                              {item.quantity}x {item.name}
                            </span>
                          ))}
                        </div>
                      </div>
                    ) : null}

                    <div style={styles.detailBlock}>
                      <span style={styles.detailLabel}>Detalle</span>
                      <div style={styles.detailText}>
                        {record.detail || "Sin detalle adicional cargado todavia."}
                      </div>
                    </div>

                    <div style={styles.actionRow}>
                      {canManagePromos ? (
                        <>
                          <button
                            type="button"
                            style={styles.secondaryButton}
                            onClick={() => startEditingDailyMenu(record)}
                          >
                            Editar
                          </button>
                          <button
                            type="button"
                            style={styles.primaryButton}
                            onClick={() => toggleRecordActive(record.id)}
                          >
                            {record.active === false ? "Activar" : "Pausar"}
                          </button>
                          <button
                            type="button"
                            style={styles.secondaryButton}
                            onClick={() => toggleRecordDelivery(record.id)}
                          >
                            {record.deliveryAvailable === true
                              ? "Quitar de Delivery"
                              : "Publicar en Delivery"}
                          </button>
                          <button
                            type="button"
                            style={styles.secondaryButton}
                            onClick={() => deleteRecord(record.id)}
                          >
                            Eliminar
                          </button>
                        </>
                      ) : (
                        <span style={styles.readOnlyInline}>Solo lectura</span>
                      )}
                    </div>
                  </article>
                ))}
              </div>
            ) : (
              <div style={styles.emptyState}>
                Todavia no cargaste un menu del dia para esta sucursal.
              </div>
            )}
          </section>
        </div>
      ) : (
        <div style={styles.layoutGrid}>
          <section style={styles.panel}>
            <div style={styles.panelHeader}>
              <div>
                <h2 style={styles.panelTitle}>
                  {editingPromotionId ? "Editar promocion" : "Nueva promocion"}
                </h2>
                <p style={styles.panelText}>
                  Carga reglas de descuento por categoria, subcategoria o producto, con
                  dias y horarios propios.
                </p>
              </div>
              <span style={styles.panelPill}>
                {editingPromotionId ? "Editando" : `${activePromotionCount} activas`}
              </span>
            </div>

            {!canManagePromos ? (
              <div style={styles.readOnlyNotice}>
                Estás viendo este módulo en solo lectura. Para crear o editar promociones
                necesitás acceso de admin general o permiso de gestión de menú.
              </div>
            ) : null}

            <form onSubmit={handleCreatePromotionRule} style={styles.formGrid}>
              <fieldset style={styles.formFieldset} disabled={!canManagePromos}>
              <label style={styles.field}>
                <span style={styles.label}>Nombre de la promocion</span>
                <input
                  type="text"
                  value={promotionRuleForm.title}
                  onChange={(event) =>
                    setPromotionRuleForm((prev) => ({ ...prev, title: event.target.value }))
                  }
                  style={styles.input}
                  placeholder="Martes de pastas"
                />
              </label>

              <label style={styles.field}>
                <span style={styles.label}>Gancho visible</span>
                <input
                  type="text"
                  value={promotionRuleForm.hook}
                  onChange={(event) =>
                    setPromotionRuleForm((prev) => ({ ...prev, hook: event.target.value }))
                  }
                  style={styles.input}
                  placeholder="50% OFF"
                />
              </label>

              <label style={styles.field}>
                <span style={styles.label}>Alcance</span>
                <select
                  value={promotionRuleForm.scopeType}
                  onChange={(event) =>
                    setPromotionRuleForm((prev) => ({
                      ...prev,
                      scopeType: event.target.value,
                      subcategoryId: "",
                      productId: "",
                    }))
                  }
                  style={styles.input}
                >
                  <option value="category">Categoria</option>
                  <option value="subcategory">Subcategoria</option>
                  <option value="product">Producto</option>
                </select>
              </label>

              <label style={styles.field}>
                <span style={styles.label}>Categoria base</span>
                <select
                  value={promotionRuleForm.categoryId}
                  onChange={(event) =>
                    setPromotionRuleForm((prev) => ({
                      ...prev,
                      categoryId: event.target.value,
                      subcategoryId: "",
                      productId: "",
                    }))
                  }
                  style={styles.input}
                >
                  <option value="">Selecciona una categoria</option>
                  {menuCategories.map((category) => (
                    <option key={category.id} value={category.id}>
                      {category.nombre}
                    </option>
                  ))}
                </select>
              </label>

              {promotionRuleForm.scopeType === "subcategory" ? (
                <label style={styles.field}>
                  <span style={styles.label}>Subcategoria</span>
                  <select
                    value={promotionRuleForm.subcategoryId}
                    onChange={(event) =>
                      setPromotionRuleForm((prev) => ({
                        ...prev,
                        subcategoryId: event.target.value,
                        productId: "",
                      }))
                    }
                    style={styles.input}
                  >
                    <option value="">Selecciona una subcategoria</option>
                    {promotionScopeSubcategories.map((subcategory) => (
                      <option key={subcategory.id} value={subcategory.id}>
                        {subcategory.nombre}
                      </option>
                    ))}
                  </select>
                </label>
              ) : null}

              {promotionRuleForm.scopeType === "product" ? (
                <>
                  <label style={styles.field}>
                    <span style={styles.label}>Buscar producto</span>
                    <input
                      type="text"
                      value={promotionSearch}
                      onChange={(event) => setPromotionSearch(event.target.value)}
                      style={styles.input}
                      placeholder="Cafe, medialuna, ravioles..."
                    />
                  </label>

                  <label style={styles.field}>
                    <span style={styles.label}>Producto</span>
                    <select
                      value={promotionRuleForm.productId}
                      onChange={(event) =>
                        setPromotionRuleForm((prev) => ({
                          ...prev,
                          productId: event.target.value,
                        }))
                      }
                      style={styles.input}
                    >
                      <option value="">Selecciona un producto</option>
                      {promotionScopeProducts.map((product) => (
                        <option key={product.id} value={product.id}>
                          {product.nombre}
                        </option>
                      ))}
                    </select>
                  </label>
                </>
              ) : null}

              <label style={styles.field}>
                <span style={styles.label}>Tipo de descuento</span>
                <select
                  value={promotionRuleForm.discountType}
                  onChange={(event) =>
                    setPromotionRuleForm((prev) => ({
                      ...prev,
                      discountType: event.target.value,
                    }))
                  }
                  style={styles.input}
                >
                  <option value="percentage">Porcentaje</option>
                  <option value="fixed">Monto fijo</option>
                </select>
              </label>

              <label style={styles.field}>
                <span style={styles.label}>Valor</span>
                <input
                  type="number"
                  min="0"
                  step="1"
                  value={promotionRuleForm.discountValue}
                  onChange={(event) =>
                    setPromotionRuleForm((prev) => ({
                      ...prev,
                      discountValue: event.target.value,
                    }))
                  }
                  style={styles.input}
                  placeholder={promotionRuleForm.discountType === "fixed" ? "1000" : "50"}
                />
              </label>

              <label style={styles.field}>
                <span style={styles.label}>Leyenda visible</span>
                <input
                  type="text"
                  value={promotionRuleForm.validity}
                  onChange={(event) =>
                    setPromotionRuleForm((prev) => ({
                      ...prev,
                      validity: event.target.value,
                    }))
                  }
                  style={styles.input}
                  placeholder="Todos los martes"
                />
              </label>

              <label style={styles.field}>
                <span style={styles.label}>Tipografia</span>
                <select
                  value={promotionRuleForm.fontPreset}
                  onChange={(event) =>
                    setPromotionRuleForm((prev) => ({
                      ...prev,
                      fontPreset: event.target.value,
                    }))
                  }
                  style={styles.input}
                >
                  {PROMOTION_FONT_OPTIONS.map((option) => (
                    <option key={option.id} value={option.id}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>

              {promotionRuleForm.fontPreset === "custom" ? (
                <label style={styles.field}>
                  <span style={styles.label}>Font family custom</span>
                  <input
                    type="text"
                    value={promotionRuleForm.customFontFamily}
                    onChange={(event) =>
                      setPromotionRuleForm((prev) => ({
                        ...prev,
                        customFontFamily: event.target.value,
                      }))
                    }
                    style={styles.input}
                    placeholder='"Bebas Neue", Arial, sans-serif'
                  />
                </label>
              ) : null}

              <label style={styles.deliveryOption}>
                <input
                  type="checkbox"
                  checked={promotionRuleForm.deliveryAvailable === true}
                  onChange={(event) =>
                    setPromotionRuleForm((prev) => ({
                      ...prev,
                      deliveryAvailable: event.target.checked,
                    }))
                  }
                  style={styles.deliveryCheckbox}
                />
                <span style={styles.deliveryOptionText}>
                  <strong>Disponible en Delivery y We! pedidos</strong>
                  <small>El descuento se mostrará y aplicará automáticamente cuando corresponda.</small>
                </span>
              </label>

              <label style={styles.field}>
                <span style={styles.label}>Descuento valido con</span>
                <select style={styles.input} value={promotionRuleForm.paymentCondition}
                  onChange={(event) => setPromotionRuleForm((prev) => ({ ...prev, paymentCondition: event.target.value }))}>
                  <option value="any">Todos los medios de pago</option>
                  <option value="cash_only">Solo efectivo</option>
                </select>
              </label>

              <div style={styles.scheduleCard}>
                <span style={styles.label}>Dias y horarios</span>
                <strong style={styles.scheduleTitle}>
                  {buildPromotionScheduleSummary(promotionRuleForm.schedule)}
                </strong>
                <span style={styles.scheduleHint}>
                  Igual que con los combos, esto vive en un modal para no ensanchar la
                  pagina.
                </span>
                <button
                  type="button"
                  style={styles.secondaryButton}
                  onClick={() => openScheduleModal("promotion")}
                >
                  Configurar dias y horarios
                </button>
              </div>

              <label style={styles.field}>
                <span style={styles.label}>Imagen de la promo</span>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handlePromotionImageSelected}
                  style={styles.fileInput}
                />
                <span style={styles.fileStatus}>
                  {promotionImageProcessing
                    ? "Preparando la imagen de la promocion..."
                    : resolvedPromotionPreviewImageUrl
                      ? `Imagen cargada${
                          promotionImageDraftName
                            ? `: ${promotionImageDraftName}`
                            : ""
                        }. Queda lista para guardar con la promocion.`
                      : "Todavia no cargaste una imagen para esta promocion."}
                </span>
              </label>

              <div style={styles.previewCard}>
                <span style={styles.label}>Vista previa</span>
                <strong
                  style={{
                    ...styles.previewTitle,
                    fontFamily: getPromotionFontFamily(promotionRuleForm),
                  }}
                >
                  {promotionRuleForm.title.trim() || "Promo de la sucursal"}
                </strong>
                <span style={styles.previewMeta}>
                  {formatDiscountSummary(
                    promotionRuleForm.discountType,
                    promotionRuleForm.discountValue || 0
                  )}
                </span>
                {resolvedPromotionPreviewImageUrl ? (
                  <img
                    src={resolvedPromotionPreviewImageUrl}
                    alt="Vista previa de la promo"
                    style={styles.previewImage}
                  />
                ) : null}
              </div>

              <label style={{ ...styles.field, gridColumn: "1 / -1" }}>
                <span style={styles.label}>Detalle</span>
                <textarea
                  value={promotionRuleForm.detail}
                  onChange={(event) =>
                    setPromotionRuleForm((prev) => ({ ...prev, detail: event.target.value }))
                  }
                  style={styles.textarea}
                  placeholder="Ejemplo: 50% en toda la subcategoria Pastas para consumir en salon."
                />
              </label>

              <div style={styles.formActions}>
                <button
                  type="button"
                  style={styles.secondaryButton}
                  onClick={resetPromotionComposer}
                >
                  {editingPromotionId ? "Cancelar edicion" : "Limpiar"}
                </button>
                <button
                  type="submit"
                  style={styles.primaryButton}
                  disabled={promotionImageProcessing}
                >
                  {editingPromotionId ? "Guardar cambios" : "Guardar promocion"}
                </button>
              </div>
              </fieldset>
            </form>
          </section>

          <section style={styles.panel}>
            <div style={styles.panelHeader}>
              <div>
                <h2 style={styles.panelTitle}>Promociones creadas</h2>
                <p style={styles.panelText}>
                  Descuentos por categoria, subcategoria o producto, sin armar una promo
                  una por una.
                </p>
              </div>
            </div>

            {promotionRecords.length > 0 ? (
              <div style={styles.recordsGrid}>
                {promotionRecords.map((record) => (
                  <article key={record.id} style={styles.recordCard}>
                    {record.imageUrl ? (
                      <img
                        src={record.imageUrl}
                        alt={`Promo ${record.title}`}
                        style={styles.recordImage}
                      />
                    ) : null}

                    <div style={styles.recordHeader}>
                      <div>
                        <span
                          style={{
                            ...styles.recordHook,
                            fontFamily: record.fontFamily || undefined,
                          }}
                        >
                          {record.hook || record.pricingSummary || "Promocion"}{record.paymentCondition === "cash_only" ? " · Solo efectivo" : ""}
                        </span>
                        <h3 style={styles.recordTitle}>{record.title}</h3>
                      </div>
                      <span
                        style={{
                          ...styles.statusBadge,
                          ...(record.active === false ? styles.statusMuted : styles.statusLive),
                        }}
                      >
                        {record.active === false ? "Pausada" : "Activa"}
                      </span>
                    </div>

                    <div style={styles.metricRow}>
                      <span style={styles.metricChip}>
                        {record.pricingSummary || formatDiscountSummary(record.discountType, record.discountValue)}
                      </span>
                      <span style={styles.metricChip}>{record.targetLabel || record.categoryName}</span>
                      <span
                        style={{
                          ...styles.metricChip,
                          ...(record.deliveryAvailable === true
                            ? styles.deliveryMetricLive
                            : styles.deliveryMetricMuted),
                        }}
                      >
                        {record.deliveryAvailable === true
                          ? "Disponible en Delivery"
                          : "Fuera de Delivery"}
                      </span>
                    </div>

                    <div style={styles.detailBlock}>
                      <span style={styles.detailLabel}>Alcance</span>
                      <div style={styles.detailText}>
                        {record.targetLabel || record.categoryName || "Sin alcance"}
                      </div>
                    </div>

                    <div style={styles.detailBlock}>
                      <span style={styles.detailLabel}>Dias y horarios</span>
                      <div style={styles.detailText}>
                        {record.scheduleSummary ||
                          buildPromotionScheduleSummary(record.schedule)}
                      </div>
                    </div>

                    <div style={styles.detailBlock}>
                      <span style={styles.detailLabel}>Detalle</span>
                      <div style={styles.detailText}>
                        {record.detail || "Sin detalle adicional cargado todavia."}
                      </div>
                    </div>

                    <div style={styles.actionRow}>
                      {canManagePromos ? (
                        <>
                          <button
                            type="button"
                            style={styles.secondaryButton}
                            onClick={() => startEditingPromotion(record)}
                          >
                            Editar
                          </button>
                          <button
                            type="button"
                            style={styles.primaryButton}
                            onClick={() => toggleRecordActive(record.id)}
                          >
                            {record.active === false ? "Activar" : "Pausar"}
                          </button>
                          <button
                            type="button"
                            style={styles.secondaryButton}
                            onClick={() => toggleRecordDelivery(record.id)}
                          >
                            {record.deliveryAvailable === true
                              ? "Quitar de Delivery"
                              : "Publicar en Delivery"}
                          </button>
                          <button
                            type="button"
                            style={styles.secondaryButton}
                            onClick={() => deleteRecord(record.id)}
                          >
                            Eliminar
                          </button>
                        </>
                      ) : (
                        <span style={styles.readOnlyInline}>Solo lectura</span>
                      )}
                    </div>
                  </article>
                ))}
              </div>
            ) : (
              <div style={styles.emptyState}>
                Todavia no hay promociones por regla creadas para esta sucursal.
              </div>
            )}
          </section>
        </div>
      )}

      {scheduleModalTarget ? (
        <div style={styles.modalOverlay} onClick={closeScheduleModal}>
          <div style={styles.modalCard} onClick={(event) => event.stopPropagation()}>
            <div style={styles.panelHeader}>
              <div>
                <h2 style={styles.panelTitle}>Dias y horarios</h2>
                <p style={styles.panelText}>
                  Marca solo los dias que quieras activar. Si no marcas ninguno, queda
                  visible todos los dias.
                </p>
                <p style={styles.panelText}>
                  Si el cierre es anterior a la apertura, termina en la madrugada del
                  dia siguiente. Domingo de 19:00 a 01:00 sigue vigente el lunes
                  hasta la 01:00, aunque el lunes no este marcado.
                </p>
              </div>
              <button type="button" style={styles.secondaryButton} onClick={closeScheduleModal}>
                Cerrar
              </button>
            </div>

            <div style={styles.modalGrid}>
              {scheduleDraft.map((day) => (
                <article key={day.id} style={styles.dayCard}>
                  <label style={styles.dayToggle}>
                    <input
                      type="checkbox"
                      checked={day.active}
                      onChange={(event) =>
                        updateScheduleDraftDay(day.id, {
                          active: event.target.checked,
                        })
                      }
                    />
                    <strong>{day.label}</strong>
                  </label>

                  {day.active ? (
                    <>
                      <label style={styles.dayToggleInline}>
                        <input
                          type="checkbox"
                          checked={day.allDay}
                          onChange={(event) =>
                            updateScheduleDraftDay(day.id, {
                              allDay: event.target.checked,
                            })
                          }
                        />
                        Todo el dia
                      </label>

                      {!day.allDay ? (
                        <div style={styles.timeGrid}>
                          <label style={styles.field}>
                            <span style={styles.label}>Desde</span>
                            <input
                              type="time"
                              value={day.apertura}
                              onChange={(event) =>
                                updateScheduleDraftDay(day.id, {
                                  apertura: event.target.value,
                                })
                              }
                              style={styles.input}
                            />
                          </label>

                          <label style={styles.field}>
                            <span style={styles.label}>Hasta</span>
                            <input
                              type="time"
                              value={day.cierre}
                              onChange={(event) =>
                                updateScheduleDraftDay(day.id, {
                                  cierre: event.target.value,
                                })
                              }
                              style={styles.input}
                            />
                          </label>
                        </div>
                      ) : null}
                    </>
                  ) : (
                    <span style={styles.dayMuted}>No participa</span>
                  )}
                </article>
              ))}
            </div>

            <div style={styles.formActions}>
              <button
                type="button"
                style={styles.secondaryButton}
                onClick={() => setScheduleDraft(createDefaultPromotionSchedule())}
              >
                Limpiar
              </button>
              <button type="button" style={styles.primaryButton} onClick={applyScheduleDraft}>
                Aplicar
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {menuLoading ? <div style={styles.loadingOverlay}>Actualizando menu de la sucursal...</div> : null}
    </div>
  );
}

const styles = {
  page: {
    display: "grid",
    gap: "24px",
    paddingBottom: "24px",
  },
  hero: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
    gap: "18px",
    alignItems: "stretch",
  },
  heroEyebrow: {
    display: "inline-flex",
    alignItems: "center",
    padding: "6px 10px",
    borderRadius: "999px",
    backgroundColor: "var(--brand-soft)",
    color: "var(--brand-primary)",
    fontSize: 12,
    fontWeight: 700,
    marginBottom: 12,
  },
  heroTitle: {
    margin: 0,
    fontSize: "clamp(28px, 4vw, 38px)",
    color: "#0f172a",
  },
  heroText: {
    margin: "12px 0 0",
    color: "#475569",
    lineHeight: 1.65,
    maxWidth: 760,
  },
  heroStats: {
    display: "grid",
    gap: "14px",
  },
  statCard: {
    backgroundColor: "#ffffff",
    border: "1px solid #e2e8f0",
    borderRadius: 16,
    padding: "18px 20px",
    boxShadow: "0 14px 34px rgba(15, 23, 42, 0.06)",
    display: "grid",
    gap: 6,
  },
  statLabel: {
    fontSize: 12,
    fontWeight: 700,
    color: "#64748b",
    textTransform: "uppercase",
    letterSpacing: "0.06em",
  },
  statValue: {
    fontSize: 30,
    color: "#0f172a",
  },
  statMeta: {
    color: "#64748b",
    fontSize: 14,
  },
  errorBanner: {
    backgroundColor: "#fef2f2",
    color: "#b91c1c",
    border: "1px solid #fecaca",
    borderRadius: 14,
    padding: "14px 16px",
  },
  feedbackBanner: {
    borderRadius: 14,
    padding: "14px 16px",
    fontWeight: 600,
  },
  feedbackSuccess: {
    backgroundColor: "#ecfdf3",
    color: "#166534",
    border: "1px solid #bbf7d0",
  },
  feedbackError: {
    backgroundColor: "#fef2f2",
    color: "#b91c1c",
    border: "1px solid #fecaca",
  },
  feedbackWarning: {
    backgroundColor: "#fff7ed",
    color: "#9a3412",
    border: "1px solid #fdba74",
  },
  tabsRow: {
    display: "inline-flex",
    flexWrap: "wrap",
    gap: 10,
  },
  tabButton: {
    border: "1px solid #cbd5e1",
    backgroundColor: "#ffffff",
    color: "#334155",
    borderRadius: 999,
    padding: "10px 18px",
    fontSize: 14,
    fontWeight: 700,
    cursor: "pointer",
  },
  tabButtonActive: {
    backgroundColor: "var(--brand-primary)",
    borderColor: "var(--brand-primary)",
    color: "var(--brand-secondary)",
  },
  layoutGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(340px, 1fr))",
    gap: "22px",
    alignItems: "start",
  },
  panel: {
    backgroundColor: "#ffffff",
    borderRadius: 18,
    border: "1px solid #e2e8f0",
    boxShadow: "0 20px 40px rgba(15, 23, 42, 0.06)",
    padding: "24px",
    display: "grid",
    gap: "18px",
  },
  panelHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: "14px",
  },
  panelTitle: {
    margin: 0,
    fontSize: 22,
    color: "#0f172a",
  },
  panelText: {
    margin: "8px 0 0",
    color: "#64748b",
    lineHeight: 1.6,
  },
  panelPill: {
    display: "inline-flex",
    alignItems: "center",
    padding: "8px 12px",
    borderRadius: 999,
    backgroundColor: "var(--brand-soft)",
    color: "var(--brand-primary)",
    fontWeight: 700,
    fontSize: 13,
    whiteSpace: "nowrap",
  },
  readOnlyNotice: {
    borderRadius: 14,
    border: "1px solid #fde68a",
    backgroundColor: "#fffbeb",
    color: "#92400e",
    padding: "14px 16px",
    lineHeight: 1.6,
  },
  formGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
    gap: "16px",
    alignItems: "start",
  },
  formFieldset: {
    gridColumn: "1 / -1",
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
    gap: "16px",
    alignItems: "start",
    border: "none",
    padding: 0,
    margin: 0,
    minWidth: 0,
  },
  field: {
    display: "grid",
    gap: "8px",
  },
  label: {
    fontSize: 13,
    fontWeight: 700,
    color: "#334155",
  },
  input: {
    width: "100%",
    minHeight: 44,
    borderRadius: 12,
    border: "1px solid #cbd5e1",
    padding: "10px 12px",
    fontSize: 14,
    color: "#0f172a",
    backgroundColor: "#ffffff",
    boxSizing: "border-box",
  },
  textarea: {
    width: "100%",
    minHeight: 110,
    borderRadius: 12,
    border: "1px solid #cbd5e1",
    padding: "12px 14px",
    fontSize: 14,
    color: "#0f172a",
    resize: "vertical",
    boxSizing: "border-box",
  },
  fileInput: {
    width: "100%",
    fontSize: 14,
    color: "#334155",
  },
  fileStatus: {
    color: "#64748b",
    lineHeight: 1.45,
    fontSize: 13,
  },
  scheduleCard: {
    gridColumn: "1 / -1",
    display: "grid",
    gap: 8,
    borderRadius: 16,
    border: "1px solid #dbeafe",
    backgroundColor: "#f8fbff",
    padding: "16px 18px",
  },
  scheduleTitle: {
    color: "#0f172a",
    fontSize: 16,
  },
  scheduleHint: {
    color: "#64748b",
    lineHeight: 1.5,
    fontSize: 14,
  },
  deliveryOption: {
    gridColumn: "1 / -1",
    display: "flex",
    alignItems: "center",
    gap: 12,
    minHeight: 58,
    padding: "12px 14px",
    border: "1px solid #b8d7c8",
    borderRadius: 8,
    backgroundColor: "#f1faf6",
    cursor: "pointer",
  },
  deliveryCheckbox: {
    width: 22,
    height: 22,
    margin: 0,
    accentColor: "#15805f",
    flex: "0 0 auto",
  },
  deliveryOptionText: {
    display: "grid",
    gap: 3,
    color: "#174536",
    lineHeight: 1.35,
  },
  previewCard: {
    display: "grid",
    gap: 10,
    borderRadius: 16,
    border: "1px solid #e2e8f0",
    backgroundColor: "#f8fafc",
    padding: "16px 18px",
  },
  previewTitle: {
    fontSize: 24,
    color: "#0f172a",
    lineHeight: 1.1,
  },
  previewMeta: {
    color: "#475569",
    fontWeight: 700,
  },
  previewImage: {
    width: "100%",
    aspectRatio: "4 / 5",
    objectFit: "contain",
    objectPosition: "center",
    borderRadius: 12,
    backgroundColor: "#ffffff",
    border: "1px solid #e2e8f0",
    display: "block",
  },
  composerPanel: {
    gridColumn: "1 / -1",
    display: "grid",
    gap: 16,
    borderRadius: 18,
    border: "1px solid #e2e8f0",
    backgroundColor: "#fbfdff",
    padding: "18px 20px",
  },
  composerHeader: {
    display: "grid",
    gap: 6,
  },
  composerTitle: {
    color: "#0f172a",
    fontSize: 16,
  },
  composerHint: {
    color: "#64748b",
    lineHeight: 1.5,
    fontSize: 14,
  },
  inlineGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
    gap: 14,
  },
  searchPanel: {
    display: "grid",
    gap: 12,
    borderRadius: 16,
    border: "1px solid #e2e8f0",
    padding: "14px 16px",
    backgroundColor: "#ffffff",
  },
  searchHeader: {
    display: "flex",
    justifyContent: "space-between",
    gap: 12,
    color: "#475569",
    fontSize: 13,
  },
  searchGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
    gap: 10,
  },
  searchCard: {
    border: "1px solid #dbe3ef",
    borderRadius: 14,
    backgroundColor: "#ffffff",
    padding: "12px 14px",
    textAlign: "left",
    display: "grid",
    gap: 4,
    cursor: "pointer",
  },
  searchCardActive: {
    borderColor: "#2563eb",
    boxShadow: "0 0 0 2px rgba(37, 99, 235, 0.12)",
  },
  searchName: {
    color: "#0f172a",
  },
  searchMeta: {
    color: "#64748b",
    fontSize: 13,
  },
  searchPrice: {
    color: "#2563eb",
    fontWeight: 700,
    fontSize: 13,
  },
  selectionSummary: {
    borderRadius: 12,
    backgroundColor: "#eff6ff",
    color: "#1d4ed8",
    padding: "10px 12px",
    fontSize: 14,
  },
  actionsInline: {
    display: "flex",
    justifyContent: "flex-start",
    gap: 10,
  },
  listStack: {
    display: "grid",
    gap: 10,
  },
  listCard: {
    borderRadius: 14,
    border: "1px solid #e2e8f0",
    backgroundColor: "#ffffff",
    padding: "12px 14px",
    display: "flex",
    justifyContent: "space-between",
    gap: 12,
    alignItems: "center",
  },
  listTitle: {
    color: "#0f172a",
  },
  listMeta: {
    marginTop: 4,
    color: "#64748b",
    fontSize: 13,
  },
  priceSummaryCard: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
    gap: 12,
    borderRadius: 16,
    backgroundColor: "#0f172a",
    color: "#ffffff",
    padding: "16px 18px",
  },
  summaryValue: {
    display: "block",
    marginTop: 4,
    fontSize: 18,
  },
  formActions: {
    gridColumn: "1 / -1",
    display: "flex",
    justifyContent: "flex-end",
    gap: 10,
    flexWrap: "wrap",
  },
  primaryButton: {
    border: "1px solid var(--brand-primary)",
    borderRadius: 12,
    backgroundColor: "var(--brand-primary)",
    color: "var(--brand-secondary)",
    padding: "12px 16px",
    fontSize: 14,
    fontWeight: 700,
    cursor: "pointer",
  },
  secondaryButton: {
    border: "1px solid #cbd5e1",
    borderRadius: 12,
    backgroundColor: "#ffffff",
    color: "#334155",
    padding: "11px 15px",
    fontSize: 14,
    fontWeight: 700,
    cursor: "pointer",
  },
  recordsGrid: {
    display: "grid",
    gap: 14,
  },
  recordCard: {
    borderRadius: 18,
    border: "1px solid #e2e8f0",
    backgroundColor: "#ffffff",
    padding: "18px",
    display: "grid",
    gap: 14,
  },
  recordImage: {
    width: "100%",
    aspectRatio: "4 / 5",
    objectFit: "contain",
    objectPosition: "center",
    borderRadius: 14,
    backgroundColor: "#ffffff",
    border: "1px solid #e2e8f0",
    display: "block",
  },
  recordHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: 12,
  },
  recordHook: {
    display: "inline-flex",
    alignItems: "center",
    padding: "6px 10px",
    borderRadius: 999,
    backgroundColor: "#eef2ff",
    color: "#4338ca",
    fontSize: 12,
    fontWeight: 700,
  },
  recordTitle: {
    margin: "8px 0 0",
    color: "#0f172a",
    fontSize: 21,
  },
  statusBadge: {
    display: "inline-flex",
    alignItems: "center",
    padding: "6px 10px",
    borderRadius: 999,
    fontSize: 12,
    fontWeight: 700,
    whiteSpace: "nowrap",
  },
  statusLive: {
    backgroundColor: "#ecfdf3",
    color: "#166534",
  },
  statusMuted: {
    backgroundColor: "#e2e8f0",
    color: "#475569",
  },
  metricRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: 8,
  },
  metricChip: {
    display: "inline-flex",
    alignItems: "center",
    padding: "7px 10px",
    borderRadius: 999,
    backgroundColor: "#f8fafc",
    color: "#334155",
    border: "1px solid #dbe3ef",
    fontSize: 13,
    fontWeight: 700,
  },
  deliveryMetricLive: {
    borderColor: "#a7d8c1",
    backgroundColor: "#eaf8f1",
    color: "#176044",
  },
  deliveryMetricMuted: {
    borderColor: "#d7dee7",
    backgroundColor: "#f4f6f8",
    color: "#667085",
  },
  detailBlock: {
    display: "grid",
    gap: 5,
  },
  detailLabel: {
    fontSize: 12,
    fontWeight: 700,
    color: "#64748b",
    textTransform: "uppercase",
    letterSpacing: "0.05em",
  },
  detailText: {
    color: "#334155",
    lineHeight: 1.6,
  },
  chipWrap: {
    display: "flex",
    flexWrap: "wrap",
    gap: 8,
  },
  comboChip: {
    display: "inline-flex",
    alignItems: "center",
    padding: "8px 10px",
    borderRadius: 999,
    backgroundColor: "#eff6ff",
    color: "#1d4ed8",
    fontSize: 13,
    fontWeight: 700,
  },
  actionRow: {
    display: "flex",
    justifyContent: "flex-end",
    gap: 10,
    flexWrap: "wrap",
  },
  readOnlyInline: {
    color: "#64748b",
    fontSize: 13,
    fontWeight: 700,
    alignSelf: "center",
  },
  emptyState: {
    borderRadius: 16,
    border: "1px dashed #cbd5e1",
    backgroundColor: "#f8fafc",
    padding: "18px 20px",
    color: "#64748b",
    lineHeight: 1.6,
  },
  modalOverlay: {
    position: "fixed",
    inset: 0,
    backgroundColor: "rgba(15, 23, 42, 0.45)",
    display: "grid",
    placeItems: "center",
    padding: 20,
    zIndex: 2000,
  },
  modalCard: {
    width: "min(980px, 100%)",
    maxHeight: "88vh",
    overflowY: "auto",
    backgroundColor: "#ffffff",
    borderRadius: 22,
    border: "1px solid #e2e8f0",
    boxShadow: "0 30px 80px rgba(15, 23, 42, 0.24)",
    padding: 24,
    display: "grid",
    gap: 18,
  },
  modalGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
    gap: 14,
  },
  dayCard: {
    borderRadius: 16,
    border: "1px solid #e2e8f0",
    backgroundColor: "#fbfdff",
    padding: "16px 16px 18px",
    display: "grid",
    gap: 12,
    alignContent: "start",
  },
  dayToggle: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    color: "#0f172a",
  },
  dayToggleInline: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    color: "#334155",
    fontSize: 14,
  },
  dayMuted: {
    color: "#94a3b8",
    fontSize: 13,
  },
  timeGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: 12,
  },
  loadingOverlay: {
    position: "fixed",
    right: 20,
    bottom: 20,
    backgroundColor: "#0f172a",
    color: "#ffffff",
    borderRadius: 999,
    padding: "12px 16px",
    boxShadow: "0 16px 36px rgba(15, 23, 42, 0.24)",
    zIndex: 1500,
  },
};

export default Promos;
